import { inject, ChangeDetectorRef, Component, Type, ElementRef, OnInit, OnDestroy, AfterViewInit, HostListener, ViewChild } from '@angular/core';
import { registerScreenActions, registerScreenPanels } from '../../studio-nav/screen-panels.service';
import { Subject, Subscription } from 'rxjs';
import { EntityComponent } from './entity/entity.component';
import { CommonModule } from '@angular/common';
import { Entity, TypeData } from '../../model/entity.model';
import { isSystemVariableKey } from '../../shared/components/atlas/atlas.utils';
import { isReservedFieldName, reservedFieldNameMessage } from '../../shared/entity/reserved-field-names.util';
// The entity page's rules live in one util, asked by this form AND the entity chat (ADR 0009, ADR 0008).
import {
  ENTITY_NAME_TAKEN_MESSAGE, LOCKED_FIELD_EDITABLE_SETTINGS, OFFERED_FIELD_TYPES, applyPermissionChange, canEditAllFieldSettings, canHaveSampleRows, canOpenFieldForEdit,
  checkEntityNameOnSave, checkNewFieldName, checkRenamedFieldName, cleanEntityNameAsTyped, cleanEntityNameOnSave,
  cleanFieldNameAsTyped, cleanFieldNameOnAdd, cleanFieldNameOnEdit, entityNameTaken, isFinalized, isFullAccessRole, isProtectedField,
  isScalarFieldType as ruleIsScalarFieldType, isSystemField, lockedFieldEditableSettings, offeredFieldTypes, referenceTargetOptions, referenceTargets,
  rolesRemovedSince, sampleRowFields,
} from './zs-entity/rules/entity-form-rules';
import { setTimestampModeChoice, timestampModeChoice, type TimestampModeChoice } from '../../shared/entity/field-default.util';
import { aobSystemFields, applyTypeDefaults, blankField, entitySkeleton, extendedAttributeFields, listFields, sampleRowSeed } from './zs-entity/rules/field-builders';
import { Role } from '../../model/role.model';
import type { Crud } from './zs-entity/zs-entity.types';
import { EntityService } from '../../services/entity.service';
import { ClientService } from '../../services/client.service';
import { OotbEntityService } from '../../services/ootb-entity.service';
import { FunctionListGlobalService } from '../../services/function-list-global.service';
import { FunctionListService } from '../../services/function-list.service';
import { FunctionInterface } from '../../model/function.model';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { GlobalVariablesService } from '../../services/global-variables.service';
import { LoadingService } from '../../loading.service';
import { RoleService } from '../../services/role.service';
import { hasOrdinaryRoles } from '../../services/role-rules';
import { environment } from '../../../environments/environment';
import { FixReport } from './sync-entities.types';
import { FEATURE_COMPONENTS } from '../../config/feature-components.config';
import { Graph, Shape } from '@antv/x6';
import { buildOverviewModel, graphChain, GraphEdge, X6_SYNC_RENDER } from './graph/entity-graph.model';
import { PendingStatusWatch } from './pending-status-watch';
import { layoutOverview } from './graph/entity-graph.layout';
import { EntityGraphComponent } from './graph/entity-graph.component';
import { EntityDetailGraphComponent } from './graph/entity-detail-graph.component';
import dagre from 'dagre';
import { DefaultValueEditorComponent } from './default-value-editor/default-value-editor.component';
import { formatShapeSummary } from '../../shared/geometry/shape-description';
import { instantFromWallInput, wallInputFromInstant } from '../../shared/time/project-clock';
import { ZsEntityChatComponent } from './zs-entity/chat/zs-entity-chat.component';
import { countUnsavedChanges, whatLeavingLoses } from './zs-entity/apply/unsaved-count';
import type { EntityChatPage } from './zs-entity/chat/entity-chat-session';
import { ConfirmWaitsNgService } from '../../services/mcp/actions/confirm-waits-ng.service';
import { ENTITY_COMMIT, ENTITY_COMMITS, ENTITY_IMPORT_COMMIT, ENTITY_PERMISSIONS_COMMIT, ENTITY_SYNC_COMMIT } from '../../services/mcp/actions/entity-confirm';
import type { EntityConfirmPage } from '../../services/mcp/actions/entity-confirm';
import { StudioNavService } from '../../studio-nav/studio-nav.service';
import { EntityPageBridgeService } from '../../services/mcp/entity-page-bridge.service';
//! has to be removed only for testing
// import { Router } from '@angular/router';
//! has to be removed only for testing

/** The chips above the entity list. */
// Only the commented-out offer below reads this; it stays with the filter code.
const ENTITY_FILTERS = ['all', 'local', 'finalized', 'new'];
const ON_DATA_ONLY = { refused: 'That is on the Data list, and an entity is open. Nothing was changed; call navigate to data first.' };

@Component({
    selector: 'app-entity-list',
    imports: [EntityComponent, CommonModule, FormsModule, EntityGraphComponent, EntityDetailGraphComponent, DefaultValueEditorComponent, ZsEntityChatComponent],
    templateUrl: './entity-list.component.html',
    styleUrl: './entity-list.component.scss',

    host: { class: 'block h-full' },
})
export class EntityListComponent implements OnInit, OnDestroy, AfterViewInit {
  flag = true;
  get darkMode(): boolean { return this.globalVariablesService.pageThemeDark; }
  set darkMode(v: boolean) { this.globalVariablesService.pageThemeDark = v; }

  // Graph view
  /**
   * Which renderer draws the entity graphs.
   *
   * X6 is the default (2026-08-25). It is the stronger graph engine — obstacle-avoiding routing,
   * capped fit, edges above nodes and a dot grid all come for free, where Foblex needed each one
   * rebuilt. Foblex remains one click away and fully live; its advantage is Angular-native nodes,
   * which is why only its cards follow the light/dark theme. See
   * docs/architecture/foblex-flow-integration.md §10.
   */
  graphEngine: 'foblex' | 'x6' = 'x6';
  selectedGraphNode: string | null = null;
  nonLinkedEntities: Entity[] = [];
  localEntities: Entity[] = [];
  hasLinkedEntities = false;
  importViewMode: 'cards' | 'table' = 'cards';
  importNodes: { id: string; name: string; type: 'entity' | 'function' | 'feature'; color: 'green' | 'blue' | 'red'; subtitle: string; importId: string; deps: string[]; dependents: string[] }[] = [];
  hoveredImportNode: string | null = null;
  highlightedIds: Set<string> = new Set();
  highlightUpstream: Set<string> = new Set();
  highlightDownstream: Set<string> = new Set();
  private static readonly statusOrder: Record<string, number> = { green: 0, blue: 1, red: 2 };
  private sortStandaloneFirst<T extends { color: string; name: string; deps: string[]; dependents: string[] }>(arr: T[]): T[] {
    return arr.slice().sort((a, b) => {
      const aStandalone = a.deps.length === 0 && a.dependents.length === 0 ? 0 : 1;
      const bStandalone = b.deps.length === 0 && b.dependents.length === 0 ? 0 : 1;
      return aStandalone - bStandalone
        || (EntityListComponent.statusOrder[a.color] ?? 3) - (EntityListComponent.statusOrder[b.color] ?? 3)
        || a.name.localeCompare(b.name);
    });
  }
  private sortByStatus<T extends { color: string; name: string }>(arr: T[]): T[] {
    return arr.slice().sort((a, b) => (EntityListComponent.statusOrder[a.color] ?? 3) - (EntityListComponent.statusOrder[b.color] ?? 3) || a.name.localeCompare(b.name));
  }
  get importEntities() { return this.sortStandaloneFirst(this.importNodes.filter(n => n.type === 'entity')); }
  get importFunctions() { return this.sortStandaloneFirst(this.importNodes.filter(n => n.type === 'function')); }
  get importFeatures() { return this.sortStandaloneFirst(this.importNodes.filter(n => n.type === 'feature')); }
  get importAllSorted() { return this.sortByStatus(this.importNodes); }
  get importDone() { return this.importNodes.filter(n => n.color === 'green').sort((a, b) => a.name.localeCompare(b.name)); }
  get importReady() { return this.importNodes.filter(n => n.color === 'blue').sort((a, b) => a.name.localeCompare(b.name)); }
  get importBlocked() { return this.importNodes.filter(n => n.color === 'red').sort((a, b) => a.name.localeCompare(b.name)); }
  get hoveredChain() {
    if (!this.hoveredImportNode) return { upstream: [], current: null, downstream: [] };
    const current = this.importNodes.find(n => n.id === this.hoveredImportNode) || null;
    const upstream = this.importNodes.filter(n => this.highlightUpstream.has(n.id));
    const downstream = this.importNodes.filter(n => this.highlightDownstream.has(n.id));
    return { upstream, current, downstream };
  }
  trackByNodeId = (_: number, node: { id: string }) => node.id;
  trackByFieldName = (_: number, field: { name: string }) => field.name;
  /** Glossary-worded Geometry summary (`Area · 4 points`, `Empty shape`, …) for the read-only cell. */
  readonly formatShapeSummary = formatShapeSummary;
  /** The Predefined button's rule, asked of the util (plan 4a). */
  readonly canHaveSampleRows = canHaveSampleRows;
  private readonly confirmWaits = inject(ConfirmWaitsNgService);
  trackByRoleId = (_: number, role: { id: string }) => role.id;
  trackByValue = (_: number, item: string) => item;
  /** Edges from the last overview build — feeds hover chain highlighting. */
  private currentEdges: GraphEdge[] = [];
  private x6Graph: Graph | null = null;
  private x6Ready = false;
  @ViewChild('x6Container') x6ContainerRef!: ElementRef;
  @ViewChild('entityGraph') entityGraph?: EntityGraphComponent;
  @ViewChild('detailGraphCmp') detailGraphCmp?: EntityDetailGraphComponent;
  @ViewChild('detailContainer') detailContainerRef!: ElementRef;
  private detailGraph: Graph | null = null;
  newData: Entity = {
    name: '',
    type: 'TABLE',
    id: '',
    status: 'NEW',
    data: [
      {
        name: 'id',
        type: 'UUID',
        isUnique: true,
        primaryKey: true,
        startIncrementFrom: 0,
        referenceEntityName: '',
        referenceEntityColumnName: 'id',
        id: '',
        data: [],
        defaultValue: '',
      },
      {
        name: 'id_auto_increment',
        type: 'AUTO_INCREMENT',
        isUnique: true,
        primaryKey: false,
        startIncrementFrom: 0,
        referenceEntityName: '',
        referenceEntityColumnName: 'id',
        id: '',
        data: [],
        defaultValue: 0,
      },
    ],
  };

  public ootb: Entity[] = [];
  public selectedEntity: string = '';
  public globalFunctions: FunctionInterface[] = [];
  public localFunctions: FunctionInterface[] = [];
  public selectedGlobalFunction: string = '';

  entityList: Entity[] | undefined;

  // Update filter state to include 'new'
  public activeFilter: 'all' | 'local' | 'finalized' | 'new' = 'all';
  public filteredEntityList: Entity[] | undefined;

  private readonly importedIdSuffixRegex = /_[A-Za-z0-9+/]{6}$/;
  importedOotbIds = new Set<string>();

  // Fix All Tables state
  fixState: 'idle' | 'running' | 'done' | 'error' = 'idle';
  fixReport: FixReport | null = null;
  fixError: string | null = null;
  showFixReport = false;

  // ── Variable editing state ──
  addVariableFlag = false;
  // Unique Key radio removed 2026-09-17 (owner) — the flag only drove that radio. Was:
  // newVariableUniqueFlag = true;
  addVariableButtonFlag = true;
  editingVariableIndex: number | null = null;
  booleanOptions = [{ label: 'True', value: true }, { label: 'False', value: false }];

  /** The date-default select: the stored mode, or 'unset' for a legacy field; picking writes the pick (zavo_bug_oct_15). */
  get timestampModeSelect(): TimestampModeChoice { return timestampModeChoice(this.newVariableData); }
  set timestampModeSelect(v: TimestampModeChoice) { setTimestampModeChoice(this.newVariableData, v); }

  /**
   * Bridges the TIMESTAMP "fixed" datetime-local input (a 'YYYY-MM-DDTHH:mm' wall clock, no zone) and the
   * field's stored defaultValue (a UTC instant). The box is the PROJECT's wall clock (CONTEXT.md **Project
   * time zone**; it was read as UTC until 2026-09-19). Seconds are always 0, as before.
   */
  get fixedTimestampLocal(): string {
    return wallInputFromInstant(this.newVariableData?.defaultValue);
  }
  set fixedTimestampLocal(v: string) {
    this.newVariableData.defaultValue = v ? instantFromWallInput(v) : '';
  }
  // ⚠ AUTO_INCREMENT, UUID and JSONB are SYSTEM column types, never offered to a builder (owner, 2026-09-17):
  // UUID and AUTO_INCREMENT exist only for `id` / `id_auto_increment`, JSONB only for AAOB storage.
  // Was (2026-09-17): dataTypes = ['STRING', 'NUMBER', 'BOOLEAN', 'ARRAY_OF_OBJECT', 'AUTO_INCREMENT', 'ENTITY_REFERENCE', 'ATTACHMENT', 'TIMESTAMP', 'GEOMETRY'];
  // Was (2026-10-02, the list moved to the rules util — ADR 0009):
  // dataTypes = ['STRING', 'NUMBER', 'BOOLEAN', 'ARRAY_OF_OBJECT', 'ENTITY_REFERENCE', 'ATTACHMENT', 'TIMESTAMP', 'GEOMETRY'];
  // Nothing reads this field now — availableDataTypes asks offeredFieldTypes directly. Kept for compatibility.
  dataTypes = [...OFFERED_FIELD_TYPES];
  newVariableData: TypeData = {
    name: '', type: 'STRING', isUnique: false, primaryKey: false, startIncrementFrom: 0,
    referenceEntityName: '', referenceEntityColumnName: 'id', multiple: false, extendedAttributes: false,
    id: '', data: [], defaultValue: '', description: '',
  };
  detailEditFlag = false;
  /** Fires whenever the entity list reloads or the editor closes — the entity chat resumes waiting edits on it (plan 4a). */
  private readonly chatWatch = new Subject<void>();
  /** Nav 7b final review (C2) — the navigator's note listener, released with the page; and the page is gone. */
  private navIntentSub: Subscription;
  private destroyed = false;
  private readonly pendingStatus = new PendingStatusWatch(
    (id, onDoc) => this.entityService.watchEntity(id, onDoc),
    (settled) => this.takeSettledStatus(settled),
  );
  /**
   * THE ENTITY CHAT'S WAY INTO THIS PAGE (ZS ENTITY, plan 3b; plan 3a final review R1–R7). The chat reads
   * and acts ONLY through this, and only through the page's own paths — so the chat and the buttons can't
   * behave differently (ADR 0009) and the chat never saves except through Save/Update (ADR 0010).
   */
  readonly chatPage: EntityChatPage = {
    clientId: () => this.clientService.selectedClientId,
    entities: () => this.entityList ?? [],
    open: () => (this.createEntityFlag ? this.newData : null),
    openSaved: () => (this.createEntityFlag && this.newData.id
      ? (this.entityList ?? []).find((e) => e.id === this.newData.id) ?? null
      : null),
    fieldFormOpen: () => this.addVariableFlag,
    adopt: (entity, opened) => {
      if (opened === 'new') {
        // Was (2026-10-03, review — FIX 3, the buttons only ever open an entity from the list, never
        // over one already open):
        // this.createEntity(entity.status === 'LOCAL' ? 'local' : 'database');
        if (this.createEntityFlag) this.exitEntity();
        this.createEntity(entity.status === 'LOCAL' ? 'local' : 'database');
      } else if (opened === 'existing') {
        const index = (this.entityList ?? []).findIndex((e) => e.id === entity.id);
        if (index < 0) return;
        // Was (2026-10-03, review — FIX 3, same as 'new': close whatever is open first):
        // this.editEntity(index);
        if (this.createEntityFlag) this.exitEntity();
        this.editEntity(index);
      }
      // AS-IS (R5): the chat's Undo compares this exact object; never re-clone or normalise it here.
      this.newData = entity;
      if (opened === 'open') this.rebuildDetailGraph();
    },
    restore: (entity) => {
      // Was (2026-10-03, review — FIX 3, the field form must close the way exitEntity does, or Undo can
      // land under a half-edited field):
      // if (!entity) { this.exitEntity(); return; }
      this.addVariableFlag = false; this.editingVariableIndex = null;
      if (!entity) { this.exitEntity(); return; }
      this.newData = entity;
      this.detailEditFlag = canEditAllFieldSettings(entity);
      this.rebuildDetailGraph();
    },
    discard: () => this.exitEntity(),
    // Was (2026-10-03, review — FIX 1, SaveTableChanges swallows a create error and still returns true,
    // and UpdateTableChanges has no failure signal at all; both success paths end in exitEntity()):
    // save: async () => {
    //   if (isFinalized(this.newData.status)) { await this.UpdateTableChanges(); return true; }
    //   return (await this.SaveTableChanges()) === true;
    // },
    save: async () => {
      // True only when the page really saved: no throw AND the editor closed (both success paths call exitEntity).
      try {
        if (isFinalized(this.newData.status)) await this.UpdateTableChanges();
        else if ((await this.SaveTableChanges()) !== true) return false;
      } catch {
        return false;
      }
      return !this.createEntityFlag;
    },
    // Was (2026-10-03, review — FIX 3, never replace a field mid-edit under the open field form):
    // editField: (path, field) => {
    //   if (!this.createEntityFlag) return;
    editField: (path, field) => { if (this.addVariableFlag) return;
      if (!this.createEntityFlag) return;
      const indices: number[] = [];
      let data = this.newData.data;
      for (const name of path) {
        const i = data.findIndex((f) => f.type === 'ARRAY_OF_OBJECT' && f.name === name);
        if (i < 0) return;
        indices.push(i);
        data = data[i].data ?? [];
      }
      const index = data.findIndex((f) => f.name === field);
      if (index >= 0) this.editVariableAt(index, { path: indices });
    },
    watch: (onChange) => {
      const sub = this.chatWatch.subscribe(() => onChange());
      return () => sub.unsubscribe();
    },
    mint: () => this.generateTableId(),
    now: () => new Date(),
    // Plan 4b — held actions: what the chat may read, and each action run through the page's OWN path
    // (ADR 0009). The confirm bar was the builder's approval, so the page's own confirm dialog is never raised.
    // Was (2026-10-04, plan 4c): roles: () => (this.roles ?? []).map((r) => ({ id: r.id, name: r.name, permissions: r.permissions ?? {} })),
    roles: () => (this.roles ?? []).map((r) => ({ id: r.id, name: r.name, permissions: r.permissions ?? {}, reserved: !!r.reserved })),
    ootb: () => this.importNodes
      .filter((n) => n.type === 'entity')
      .map((n) => ({ id: n.importId, name: n.name, deps: n.deps.map((d) => d.replace(/^ootb_/, '')), imported: n.color === 'green' })),
    permissionsOpen: () => this.showRolePermsModal || this.showAddRoleModal,
    // Plan 6 — the page's Predefined rows window is open.
    sampleRowsOpen: () => this.showPredefinedModal,
    runImport: async (ids) => {
      // confirmImport()'s steps, once per entity, dependencies first (the chat ordered them).
      const done: string[] = [];
      try {
        for (const id of ids) {
          // Plan 4b fix round 1 (F3) — imported meanwhile: never re-create a live entity (parity); the
          // page itself never offers a green (already-imported) node for the builder to import either.
          if (this.isOotbAlreadyImported(id)) continue;
          this.selectedEntity = id;
          await this.addOOTBEntity();
          done.push(this.ootb.find((o) => o.id === id)?.name ?? id);
        }
        return { done, error: null };
      } catch (e) {
        // Was (2026-10-03, plan 4b Task 5 fix round 1 — F7, `??` never falls through a falsy-but-present
        // message (e.g. ''), where `||` does):
        // return { done, error: (e as Error)?.message ?? 'Unknown error' };
        return { done, error: (e as Error)?.message || 'Unknown error' };
      } finally {
        this.buildImportGraph();
      }
    },
    runSync: async () => {
      if (this.fixState === 'running') return { report: null, error: 'Sync is already running.' };
      await this.runSyncEntities();
      return { report: this.fixState === 'done' ? this.fixReport : null, error: this.fixState === 'error' ? this.fixError : null };
    },
    // Was (2026-10-03, plan 4b Task 5 fix round 1 — F1/F2/F4, wrote the chat's `next` row AS-IS: built from
    // the chat's possibly stale roles() snapshot, it could silently undo a permission granted elsewhere
    // meanwhile, and reported success on a removal that changed nothing):
    // runPermissions: async (entityId, roleId, next) => {
    //   // The Role Permissions modal's own steps: load, add or remove the role, set its bits, Save.
    //   if (!this.createEntityFlag || this.newData?.id !== entityId) return false;
    //   await this.loadRolePermissions();
    //   if (next === null) {
    //     this.selectedRolesForEntity = this.selectedRolesForEntity.filter((r) => r.id !== roleId);
    //     delete this.rolePermissions[roleId];
    //   } else {
    //     const role = this.roles.find((r) => r.id === roleId);
    //     if (role && !this.isRoleAddedToEntity(roleId)) this.selectedRolesForEntity.push(role);
    //     this.rolePermissions[roleId] = { ...next };
    //   }
    //   const saved = await this.saveRolePermissions();
    //   this.roleService.query(1000); // refresh the roles the chat reads next
    //   return saved;
    // },
    runPermissions: async (entityId, roleId, change) => {
      // The Role Permissions modal's own steps (load, add or remove the role, set its bits, Save), applying
      // ONLY the approved change to the row as loaded now (plan 4b fix round 1).
      const refused = { saved: false, now: null as Crud | null };
      if (!this.createEntityFlag || this.newData?.id !== entityId) return refused;
      const known = this.roles.find((r) => r.id === roleId)?.permissions?.[entityId] ?? null;
      await this.loadRolePermissions();
      if (this.newData?.id !== entityId) return refused; // another entity was opened meanwhile (F4)
      const loaded = this.rolePermissions[roleId] ?? null;
      if (known && !loaded) return refused; // the load came back without a row we know exists — never write over it (F2)
      // Was (2026-10-03, plan 4b final review — I3, a revoke on a role not loaded still wrote an all-false row):
      // if (change.op === 'remove_role' && !loaded) return refused; // nothing to remove (F2)
      if (change.op !== 'grant' && !loaded) return refused; // final review (I3) — revoke/remove on a role not on the entity changes nothing, and must write nothing
      const now = applyPermissionChange(loaded, change);
      if (now === null) {
        this.selectedRolesForEntity = this.selectedRolesForEntity.filter((r) => r.id !== roleId);
        delete this.rolePermissions[roleId];
      } else {
        const role = this.roles.find((r) => r.id === roleId);
        if (role && !this.isRoleAddedToEntity(roleId)) this.selectedRolesForEntity.push(role);
        this.rolePermissions[roleId] = now;
      }
      const saved = await this.saveRolePermissions();
      return { saved, now: saved ? now : null };
    },
    // Plan 4b final review (I1) — the chat asks for one fresh read when this list lags a Finalize (N22).
    refreshEntities: () => { this.entityService.query(1000); },
  };
  /** What only the confirm lane asks of this page. It fills forms and opens dialogs; the page's own buttons commit. */
  readonly confirmPage: EntityConfirmPage = {
    showSampleRows: () => { this.showPredefinedModal = true; },
    prefillRolePermissions: async (rows) => {
      this.showRolePermsModal = true;
      await this.loadRolePermissions();
      for (const row of rows) {
        const role = this.roles.find((r) => r.id === row.roleId);
        if (role && !this.isRoleAddedToEntity(row.roleId)) this.selectedRolesForEntity.push(role);
        const had = this.rolePermissions[row.roleId] ?? { create: false, read: false, update: false, delete: false };
        // A bit the row leaves out stays as loaded, so a grant can't quietly take another away.
        this.rolePermissions[row.roleId] = {
          create: row.create ?? had.create, read: row.read ?? had.read, update: row.update ?? had.update, delete: row.delete ?? had.delete,
        };
      }
    },
    prefillImport: (importId) => {
      const node = this.importNodes.find((n) => n.type === 'entity' && n.color === 'blue' && (n.importId === importId || n.name === importId));
      if (!node) return false;
      this.onImportCardClick(node);
      return true;
    },
    syncRunning: () => this.fixState === 'running',
  };
  showExitWarning = false;
  // Path-based target so the detail editor can address a container at ANY nesting depth.
  // path = [] → the entity root (newData.data); [i] → newData.data[i].data (a top-level AOB);
  // [i, j] → newData.data[i].data[j].data (a nested AOB), and so on.
  addFieldTarget: { path: number[] } = { path: [] };
  saveNameError = '';
  /** Why the last Save or Update failed, on the page next to the buttons. */
  saveFailure = '';
  private detailEventListener: ((e: Event) => void) | null = null;

  constructor(
    public entityService: EntityService,
    private clientService: ClientService,
    private ootbEntityService: OotbEntityService,
    private functionListGlobalService: FunctionListGlobalService,
    private functionListService: FunctionListService,
    private router: Router,
    private authService: AuthService,
    private globalVariablesService: GlobalVariablesService,
    private loading: LoadingService,
    private roleService: RoleService,
    private nav: StudioNavService,
    // Nav 7b live check (L2) — report: no ChangeDetectorRef existed on this component; injected fresh as `cdr`.
    private cdr: ChangeDetectorRef,
    private entityPageBridge: EntityPageBridgeService,
  ) {
    this.loadGlobalFunctions();
    this.loadLocalFunctions();
    this.entityService.$values.subscribe((value: any) => {
      this.entityList = value;
      this.applyFilter();
      if (this.entityList)
        this.entityService.completeFinalizedEntityList =
          this.finalizedEntityList(this.entityList);

      this.loadOotbEntities();
      this.buildGraph();
      this.chatWatch.next();
      this.openFromNavigator();
      // This subscription outlives the page, and a destroyed page must not open listeners nobody will close.
      if (!this.destroyed) this.pendingStatus.follow(this.entityList ?? []);
    });
    // Was (2026-10-05, nav 7b final review, C2 — never released, so a destroyed Data page still took the next note):
    // this.nav.intent$.subscribe(() => this.openFromNavigator());
    this.navIntentSub = this.nav.intent$.subscribe(() => this.openFromNavigator());
  }

  /** The server finished a table: only its status reaches an open form, so unsaved edits there are kept. */
  private takeSettledStatus(settled: Entity): void {
    if (this.newData?.id === settled.id) {
      this.newData.status = settled.status;
      this.newData.statusReason = settled.statusReason;
    }
    this.entityService.replaceLoaded(settled);
  }

  /** Nav 7b — open the entity the navigator's note names, once the list has it (spec Q1). Same path as the chat's adopt. */
  private openFromNavigator(): void {
    // Nav 7b final review (C2) — the old `$values` subscription outlives the page and still calls this.
    if (this.destroyed) return;
    const i = this.nav.intent$.value;
    if (i?.kind !== 'entity' || !this.entityList) return;
    const index = this.entityList.findIndex((e) => e.id === i.id);
    // Was (2026-10-05, nav 7b T3 review, M4): if (index < 0) return;
    if (index < 0) {
      // The list has loaded and the named entity isn't in it — the note must not wait forever.
      // An empty list is still LOADING, not "not found", so keep waiting for it as before.
      if (this.entityList.length > 0) this.nav.take('entity');
      return;
    }
    this.nav.take('entity');
    if (this.createEntityFlag) this.exitEntity();
    this.editEntity(index);
    // Nav 7b final review (I5) — an entity opened with no route change: the entity chat re-checks its hand-off.
    this.chatWatch.next();
    // Nav 7b live check (L2) — the note was acted on inside an async chain (go() now runs in the zone, but a
    // same-URL navigation still schedules nothing of its own): force the redraw instead of waiting for one.
    this.cdr.markForCheck();
  }

  private getIdCandidates(id: string): string[] {
    if (!id) return [];
    const out = new Set<string>();
    out.add(id);
    if (this.importedIdSuffixRegex.test(id)) {
      out.add(id.slice(0, -7));
    }
    return [...out];
  }

  private getExistingEntityIdCandidates(): Set<string> {
    const existing = new Set<string>();
    for (const entity of this.entityList ?? []) {
      for (const candidate of this.getIdCandidates(entity.id)) {
        existing.add(candidate);
      }
    }
    return existing;
  }

  private async loadOotbEntities() {
    const entities = await this.ootbEntityService.query(1000);
    this.importedOotbIds = this.getExistingEntityIdCandidates();
    // Hidden control entities (tenant / invite) are auto-provisioned, never offered in the import picker.
    this.ootb = (entities ?? []).filter((e) => !e.hidden);

    // If selected entity is already imported, reset selection
    if (this.selectedEntity && this.isOotbAlreadyImported(this.selectedEntity)) {
      this.selectedEntity = '';
    }
    this.buildImportGraph();
  }

  isOotbAlreadyImported(ootbEntityId: string): boolean {
    return !!ootbEntityId && this.importedOotbIds.has(ootbEntityId);
  }

  // Load local (already imported) functions
  async loadLocalFunctions() {
    this.functionListService.$values.subscribe((value: any) => {
      this.localFunctions = value || [];
    });
  }

  //! has to be removed only for testing
  //     ngOnInit(){
  // setTimeout(() => {
  //   this.router.navigate(['dashboard/pages']);

  // },200);
  //     }
  //! has to be removed only for testing
  finalizedEntityList(list: Entity[]) {
    // Was (2026-10-02, ADR 0009 — referenceTargets decides; ⚠ N6: hidden entities stay included):
    // var returnList: { name: string; id: string }[] = [];
    // for (let i = 0; i < list.length; i++) {
    //   if (list[i].status == 'READY' || list[i].status == 'PENDING' || list[i].status == 'SYNCED' || list[i].status == 'ERROR') {
    //     if (list[i].type == 'TABLE') {
    //       returnList.push({ name: list[i].name, id: list[i].id });
    //     }
    //     for (let j = 0; j < list[i].data.length; j++) {
    //       if (list[i].data[j].type == 'ARRAY_OF_OBJECT') {
    //         // returnList.push(list[i].name + '_' + list[i].data[j].name);
    //         // returnList = JSON.parse(
    //         //   JSON.stringify(
    //         //     returnList.concat(
    //         //       this.finalizedEntityListByArrayOfObjects(
    //         //         list[i].data[j].data,
    //         //         list[i].name + '_' + list[i].data[j].name
    //         //       )
    //         //     )
    //         //   )
    //         // );
    //       }
    //     }
    //   }
    // }
    // return returnList;
    return referenceTargets(list);
  }

  finalizedEntityListByArrayOfObjects(list: TypeData[], tableName: string) {
    var returnList: string[] = [];
    for (let i = 0; i < list.length; i++) {
      if (list[i].type == 'ARRAY_OF_OBJECT') {
        returnList.push(tableName + '_' + list[i].name);
        returnList = JSON.parse(
          JSON.stringify(
            returnList.concat(
              this.finalizedEntityListByArrayOfObjects(
                list[i].data,
                tableName + '_' + list[i].name,
              ),
            ),
          ),
        );
      }
    }
    return returnList;
  }

  public createEntityFlag = false;
  // kind chosen up front: 'database' → status NEW (Finalize → synced; AOB/references allowed);
  // 'local' → status LOCAL from the start (scalar-only, predefined rows, no Finalize/Keep-Local).
  createEntity(kind: 'database' | 'local' = 'database') {
    // Was (2026-10-03, plan 3a — the skeleton lives in field-builders so the entity chat builds the same one):
    // this.newData = {
    //   name: '',
    //   type: 'TABLE',
    //   id: '',
    //   status: kind === 'local' ? 'LOCAL' : 'NEW',
    //   data: [
    //     {
    //       name: 'id',
    //       type: 'UUID',
    //       isUnique: true,
    //       primaryKey: true,
    //       startIncrementFrom: 0,
    //       referenceEntityName: '',
    //       referenceEntityColumnName: 'id',
    //       id: '',
    //       data: [],
    //       defaultValue: '',
    //     },
    //     {
    //       name: 'id_auto_increment',
    //       type: 'AUTO_INCREMENT',
    //       isUnique: true,
    //       primaryKey: false,
    //       startIncrementFrom: 0,
    //       referenceEntityName: '',
    //       referenceEntityColumnName: 'id',
    //       id: '',
    //       data: [],
    //       defaultValue: 0,
    //     },
    //   ],
    // };
    this.newData = entitySkeleton(kind);
    // Was (2026-10-02, ADR 0009): this.detailEditFlag = true;
    this.detailEditFlag = canEditAllFieldSettings(this.newData); // true: no id until the first save
    this.createEntityFlag = true;
    setTimeout(() => this.initDetailGraph(), 0);
  }

  // ── LOCAL: scalar-only type list + predefined seed rows ───────────────────────
  // LOCAL entities are scalar-only (no reference / nested-list) — they're created as LOCAL, so the
  // add-field type list is filtered. predefinedData rows are persisted with the entity and auto-fill
  // entity-list variables at runtime (ui-editor seedPredefinedData).
  showPredefinedModal = false;
  private panels = registerScreenPanels({
    screen: 'entity',
    holds: () => (this.createEntityFlag
      ? {
        entity: { id: this.newData.id, name: this.newData.name, status: this.newData.status, fields: (this.newData.data ?? []).map((f: any) => ({ name: f.name, type: f.type })) },
        entityName: this.newData.name,
        unsavedChanges: countUnsavedChanges(this.chatPage.openSaved(), this.newData),
      }
      : {
        entities: (this.entityList ?? []).map((e) => ({ id: e.id, name: e.name, status: e.status })),
        filter: this.activeFilter,
        graphEngine: this.graphEngine,
      }),
    here: () => {
      if (this.showPredefinedModal) return { panel: 'predefined-rows' };
      if (this.showRolePermsModal) return { panel: 'role-permissions' };
      return {};
    },
    // Each only when the open entity offers its button.
    select: (name) => {
      if (!this.createEntityFlag) return false;
      if (name === 'predefined-rows') {
        if (!this.canHaveSampleRows(this.newData)) return false;
        this.showPredefinedModal = true;
      } else {
        if (!this.newData?.id) return false;
        this.openRolePermsModal();
      }
      return true;
    },
  });
  private offers = registerScreenActions({
    // Was (2026-10-30), until a real run showed nothing on this screen draws filteredEntityList or activeFilter, so "Showing" was untrue:
    // 'filter-entities': async (values) => {
    //   if (this.createEntityFlag) return ON_DATA_ONLY;
    //   const filter = String(values['filter'] ?? '');
    //   if (!ENTITY_FILTERS.includes(filter)) return { refused: `Entities can be filtered by ${ENTITY_FILTERS.join(', ')}. Nothing was changed.` };
    //   this.setFilter(filter);
    //   return { done: `Showing ${filter} entities.`, result: { filter } };
    // },
    'toggle-graph-engine': async (values) => {
      if (this.createEntityFlag) return ON_DATA_ONLY;
      const graphEngine = values['graphEngine'];
      if (graphEngine !== 'foblex' && graphEngine !== 'x6') return { refused: 'The graph engine is foblex or x6. Nothing was changed.' };
      // The button sits on the graph, which is only drawn once entities are linked.
      if (!this.hasLinkedEntities) return { refused: 'The graph only shows once entities are linked, so there is no engine to switch. Nothing was changed.' };
      if (this.graphEngine === graphEngine) return { done: `The graph already uses ${graphEngine}.`, result: { graphEngine, alreadySet: true } };
      this.toggleGraphEngine();
      return { done: `The graph now uses ${graphEngine}.`, result: { graphEngine } };
    },
    'reload-list': async () => {
      if (this.createEntityFlag) return ON_DATA_ONLY;
      // The read fails quietly and leaves the old list up, so only its answer tells a reload from a failure.
      if (!(await this.entityService.query(1000))) throw new Error('The entities could not be read. The list on screen may be out of date.');
      return { done: 'Reloaded the entities.' };
    },
  });
  isScalarFieldType(t: string): boolean {
    // Was (2026-10-02, ADR 0009): return t === 'STRING' || t === 'NUMBER' || t === 'BOOLEAN' || t === 'TIMESTAMP';
    return ruleIsScalarFieldType(t);
  }
  get availableDataTypes(): string[] {
    // Was (2026-09-17):
    // if ((this.newData as any)?.status === 'LOCAL') return this.dataTypes.filter((t) => t !== 'ENTITY_REFERENCE' && t !== 'ARRAY_OF_OBJECT');
    // return this.dataTypes;
    // Was (2026-10-02, ADR 0009 — offeredFieldTypes decides, including the stored-type rule below):
    // const offered = (this.newData as any)?.status === 'LOCAL'
    //   ? this.dataTypes.filter((t) => t !== 'ENTITY_REFERENCE' && t !== 'ARRAY_OF_OBJECT')
    //   : this.dataTypes;
    // ⚠ AN EDITED FIELD ALWAYS SEES ITS OWN STORED TYPE — owner, 2026-09-17. A field saved as a type the list
    // no longer offers (AUTO_INCREMENT, UUID, JSONB) would otherwise open with a blank Field Type and could be
    // re-saved as something else. Read from the stored field, not newVariableData, so changing the select
    // away and back never loses it.
    const stored = this.editingVariableIndex !== null ? this.getTargetDataArray()[this.editingVariableIndex]?.type : null;
    // Was (2026-10-02, ADR 0009): return stored && !offered.includes(stored) ? [...offered, stored] : offered;
    return offeredFieldTypes((this.newData as any)?.status, stored ?? null);
  }
  // Was (2026-10-02, final review — the template's own ngIf/ngFor pair moves into referenceTargetOptions):
  // (the template filtered entityService.completeFinalizedEntityList with *ngIf="e.name !== newData.name")
  // Was (2026-10-16, self-reference): get referenceTargetChoices(): { name: string; id: string }[] {
  //   return referenceTargetOptions(this.entityService.completeFinalizedEntityList, this.newData?.name ?? '');
  get referenceTargetChoices(): { name: string; id: string; label: string }[] {
    return referenceTargetOptions(this.entityService.completeFinalizedEntityList, this.newData?.id ?? '');
  }
  // Was (2026-10-02, final review — isFinalized decides, same four statuses):
  // the Update button's *ngIf was newData.status === 'READY' || newData.status === 'PENDING' || newData.status === 'SYNCED' || newData.status === 'ERROR'
  get openEntityIsFinalized(): boolean {
    return isFinalized(this.newData?.status);
  }
  get predefinedFields(): any[] {
    // Was (2026-10-02, final review, ADR 0009 — isProtectedField decides, same three names):
    // return ((this.newData as any)?.data || []).filter(
    //   (f: any) => this.isScalarFieldType(f.type) && f.name !== 'id' && f.name !== 'id_auto_increment' && f.name !== 'parent_id'
    // );
    // Was (2026-10-03, plan 4a — the util owns this):
    // return ((this.newData as any)?.data || []).filter(
    //   (f: any) => this.isScalarFieldType(f.type) && !isProtectedField(f.name)
    // );
    return sampleRowFields((this.newData as any)?.data);
  }
  get predefinedRows(): any[] {
    const nd: any = this.newData;
    if (!nd) return [];
    if (!Array.isArray(nd.predefinedData)) nd.predefinedData = [];
    return nd.predefinedData;
  }
  addPredefinedRow(): void {
    const nd: any = this.newData;
    if (!Array.isArray(nd.predefinedData)) nd.predefinedData = [];
    // Was (2026-10-03, plan 4a — sampleRowSeed in field-builders, same seed):
    // const row: any = {};
    // for (const f of this.predefinedFields) {
    //   // BOOLEAN is `false` here, not `true` as in page.service: this seeds a SAMPLE ROW, not a
    //   // column default. The two tables are deliberately separate — only the "is '' a default?"
    //   // question is shared, and this line already answered it correctly.
    //   //
    //   // ⚠ THE DIVERGENCE WIDENED 2026-09-12. `defaultValueForField` now reads `defaultValue ?? null`
    //   // for STRING and NUMBER, so a field with "No default (null)" ticked seeds null on a real
    //   // record — but still seeds 0 / '' HERE, and that is intended. A predefined row is sample data
    //   // a builder is shown and edits; a blank cell in it would look like a bug, not like an absent
    //   // value. Do not "unify" these.
    //   row[f.name] = f.defaultValue !== undefined && f.defaultValue !== ''
    //     ? f.defaultValue
    //     : f.type === 'NUMBER' ? 0
    //     : f.type === 'BOOLEAN' ? false
    //     : f.type === 'GEOMETRY' ? { type: 'Polygon', coordinates: [] }
    //     : '';
    // }
    // nd.predefinedData.push(row);
    nd.predefinedData.push(sampleRowSeed(this.predefinedFields));
  }
  removePredefinedRow(i: number): void {
    const nd: any = this.newData;
    if (Array.isArray(nd.predefinedData)) nd.predefinedData.splice(i, 1);
  }
  /** A predefined row's datetime box shows and takes the PROJECT's wall clock, and stores the UTC instant. */
  timestampInputValue(v: unknown): string { return wallInputFromInstant(v); }
  timestampFromInput(v: string): string { return instantFromWallInput(v); }

  editEntity(index: number) {
    if (this.entityList) {
      this.newData = JSON.parse(JSON.stringify(this.entityList[index]));
      // Was (2026-10-02, ADR 0009): this.detailEditFlag = this.newData.status === 'NEW';
      this.detailEditFlag = canEditAllFieldSettings(this.newData);
      this.createEntityFlag = true;
      // Preload role permissions so the header badge shows a count without opening the modal.
      if (this.newData.id) this.loadRolePermissions();
      setTimeout(() => this.initDetailGraph(), 0);
    }
  }
  /** The builder's own Back. `exitEntity` stays unconditional for the paths that already saved or already asked. */
  leaveEntity() {
    if (this.nav.confirmLeave()) this.exitEntity();
  }
  exitEntity() {
    this.removeDetailEventListener();
    if (this.detailGraph) { this.detailGraph.dispose(); this.detailGraph = null; }
    this.createEntityFlag = false;
    this.addVariableFlag = false;
    this.editingVariableIndex = null;
    this.showRolePermsModal = false;
    this.showAddRoleModal = false;
    this.showPredefinedModal = false;
    setTimeout(() => this.initX6Graph(), 0);
    this.chatWatch.next();
    // Already answered if this exit followed a save, so this reaches a wait only when the builder left without one.
    this.confirmWaits.report(ENTITY_COMMIT, { outcome: 'discarded' });
    this.confirmWaits.report(ENTITY_PERMISSIONS_COMMIT, { outcome: 'discarded' });
  }

  // ══════════════════════════════════════════
  //  Role Permissions (CRUD-per-role for this entity)
  //  Ported from EntityComponent — the live entity editor is here now, so the "Add Role"
  //  UI moved with it. Permissions are stored on the ROLE docs (role.permissions[entityId])
  //  and persisted via RoleService → setPermissions (gateway role_permissions), NOT on the
  //  entity. Applies to a SAVED entity (needs newData.id); NEW entities have no id yet.
  // ══════════════════════════════════════════
  roles: Role[] = [];
  rolePermissions: { [roleId: string]: { create: boolean; read: boolean; update: boolean; delete: boolean } } = {};
  selectedRolesForEntity: Role[] = []; // roles added to this entity for permission management
  showAddRoleModal = false;
  showRolePermsModal = false;
  rolePermsSaving = false;
  /** Plan 4b, Task 1 — the roles on this entity when its permissions were loaded or last saved. */
  private persistedRoleIds: string[] = [];

  /** Open the Role Permissions modal and (re)load this entity's permissions from all roles. */
  openRolePermsModal() {
    this.showRolePermsModal = true;
    this.loadRolePermissions();
  }

  /** Every way out of the modal that isn't its Save. */
  closeRolePermsModal() {
    this.showRolePermsModal = false;
    this.confirmWaits.report(ENTITY_PERMISSIONS_COMMIT, { outcome: 'discarded' });
  }

  getRolePermission(roleId: string, permissionType: 'create' | 'read' | 'update' | 'delete'): boolean {
    return this.rolePermissions[roleId]?.[permissionType] ?? false;
  }

  toggleRolePermission(roleId: string, permissionType: 'create' | 'read' | 'update' | 'delete') {
    if (!this.rolePermissions[roleId]) {
      this.rolePermissions[roleId] = { create: false, read: false, update: false, delete: false };
    }
    this.rolePermissions[roleId][permissionType] = !this.rolePermissions[roleId][permissionType];
  }

  /** True when any CRUD bit is set for a role — drives the row highlight. */
  hasAnyPermission(roleId: string): boolean {
    const perms = this.rolePermissions[roleId];
    return perms ? (perms.create || perms.read || perms.update || perms.delete) : false;
  }

  /** Load this entity's permissions across every role into rolePermissions + selectedRolesForEntity. */
  async loadRolePermissions() {
    if (!this.newData?.id) return;
    const permissions = await this.roleService.getEntityPermissionsAcrossRoles(this.newData.id);
    this.rolePermissions = {};
    this.selectedRolesForEntity = [];
    permissions.forEach((rolePermission) => {
      if (rolePermission.permissions) {
        this.rolePermissions[rolePermission.roleId] = rolePermission.permissions;
        const role = this.roles.find((r) => r.id === rolePermission.roleId);
        if (role) this.selectedRolesForEntity.push(role);
      }
    });
    this.persistedRoleIds = Object.keys(this.rolePermissions); // plan 4b, Task 1 — what Save compares with
  }

  toggleAddRoleModal() {
    this.showAddRoleModal = !this.showAddRoleModal;
  }

  isRoleAddedToEntity(roleId: string): boolean {
    return this.selectedRolesForEntity.some((r) => r.id === roleId);
  }

  addRoleToEntity(role: Role) {
    if (!this.isRoleAddedToEntity(role.id)) {
      this.selectedRolesForEntity.push(role);
      if (!this.rolePermissions[role.id]) {
        this.rolePermissions[role.id] = { create: false, read: false, update: false, delete: false };
      }
    }
    this.toggleAddRoleModal();
  }

  removeRoleFromEntity(roleId: string) {
    if (confirm('Remove this role from the entity? All permissions will be cleared.')) {
      this.selectedRolesForEntity = this.selectedRolesForEntity.filter((r) => r.id !== roleId);
      delete this.rolePermissions[roleId];
    }
  }

  /** Roles not yet added to this entity — the Add-Role modal list. */
  getAvailableRoles(): Role[] {
    // Was (2026-10-04, plan 4c, Q37): return this.roles.filter((role) => !this.isRoleAddedToEntity(role.id));
    return this.roles.filter((role) => !this.isRoleAddedToEntity(role.id) && !isFullAccessRole(role)); // plan 4c (Q37)
  }

  /** Plan 4d (Q44) — no role besides the built-in admin exists, so there is nothing to add (N24). */
  noOrdinaryRoles(): boolean {
    return !hasOrdinaryRoles(this.roles ?? []);
  }

  // Was (2026-10-03, plan 4b Task 1 — a removed role kept its stored access, because only the roles still
  // listed were written; and the entity chat needs to know whether the save landed):
  // async saveRolePermissions() {
  //   if (!this.newData?.id) {
  //     alert('Please save the entity first before setting permissions');
  //     return;
  //   }
  //   try {
  //     this.rolePermsSaving = true;
  //     await this.dowWithLoadingScreen(async () => {
  //       await this.roleService.updateEntityPermissionsForMultipleRoles(this.newData.id, this.rolePermissions);
  //     });
  //     this.showRolePermsModal = false;
  //   } catch (error) {
  //     console.error('Error saving role permissions:', error);
  //     alert('Failed to save permissions. Please try again.');
  //   } finally {
  //     this.rolePermsSaving = false;
  //   }
  // }
  /** True when the permissions were saved. A role removed since load loses its grant for this entity. */
  async saveRolePermissions(): Promise<boolean> {
    if (!this.newData?.id) {
      alert('Please save the entity first before setting permissions');
      return false;
    }
    const entityId = this.newData.id;
    this.confirmWaits.saving(ENTITY_PERMISSIONS_COMMIT);
    try {
      this.rolePermsSaving = true;
      await this.dowWithLoadingScreen(async () => {
        for (const roleId of rolesRemovedSince(this.persistedRoleIds, Object.keys(this.rolePermissions))) {
          await this.roleService.removeEntityPermissions(roleId, entityId);
        }
        await this.roleService.updateEntityPermissionsForMultipleRoles(entityId, this.rolePermissions);
      });
      this.persistedRoleIds = Object.keys(this.rolePermissions);
      this.roleService.query(1000); // plan 4b fix round 1 — the chat reads roles from this list; keep it current after any save
      this.showRolePermsModal = false;
      this.confirmWaits.report(ENTITY_PERMISSIONS_COMMIT, { outcome: 'saved', saved: { entityId, permissions: JSON.parse(JSON.stringify(this.rolePermissions)) } });
      return true;
    } catch (error) {
      console.error('Error saving role permissions:', error);
      alert('Failed to save permissions. Please try again.');
      this.confirmWaits.report(ENTITY_PERMISSIONS_COMMIT, { outcome: 'failed', reason: (error as Error)?.message || 'Failed to save permissions.' });
      return false;
    } finally {
      this.rolePermsSaving = false;
    }
  }

  detailZoomIn() { this.graphEngine === 'foblex' ? this.detailGraphCmp?.zoomIn() : this.detailGraph?.zoom(0.1); }
  detailZoomOut() { this.graphEngine === 'foblex' ? this.detailGraphCmp?.zoomOut() : this.detailGraph?.zoom(-0.1); }
  detailZoomFit() { this.graphEngine === 'foblex' ? this.detailGraphCmp?.zoomFit() : this.detailGraph?.zoomToFit({ padding: 40, maxScale: 1.2 }); }

  /**
   * Delete a field from the Foblex detail graph. Reproduces the confirmation the X6 path performs
   * inside its document-level `detail-field-action` listener, which stays live for the X6 engine.
   */
  onDetailDeleteField(e: { path: number[]; index: number }) {
    const data = this.resolveContainerData(e.path);
    if (confirm(`Delete field "${data[e.index]?.name}"?`)) {
      this.deleteVariableAt(e.index, { path: e.path });
    }
  }

  // ══════════════════════════════════════════
  //  Variable CRUD (reuses EntityComponent logic)
  // ══════════════════════════════════════════

  /** Field array of the container at `path` (walk newData.data[p0].data[p1]…). path=[] → entity root. */
  private resolveContainerData(path: number[]): TypeData[] {
    let arr: TypeData[] = this.newData.data;
    for (const idx of path) {
      const c = arr[idx];
      if (!c || !Array.isArray(c.data)) return arr;
      arr = c.data;
    }
    return arr;
  }

  /** The container FIELD (the AOB) at `path`; null for the entity root (empty path). */
  private resolveContainerField(path: number[]): TypeData | null {
    let arr: TypeData[] = this.newData.data;
    let field: TypeData | null = null;
    for (const idx of path) {
      field = arr[idx] ?? null;
      if (!field || !Array.isArray(field.data)) return field;
      arr = field.data;
    }
    return field;
  }

  private getTargetDataArray(): TypeData[] {
    return this.resolveContainerData(this.addFieldTarget.path);
  }

  get addFieldTargetName(): string {
    if (!this.addFieldTarget.path.length) return this.newData.name || 'Entity';
    return this.resolveContainerField(this.addFieldTarget.path)?.name || 'AOB';
  }

  addVariableChangeFlag(target?: { path: number[] }) {
    if (target) this.addFieldTarget = target;
    this.editingVariableIndex = null;
    // Was (2026-10-02, ADR 0009):
    // this.newVariableData = {
    //   name: '', type: 'STRING', isUnique: false, primaryKey: false, startIncrementFrom: 0,
    //   referenceEntityName: '', referenceEntityColumnName: 'id', extendedAttributes: false,
    //   id: '', data: [], defaultValue: '', description: '',
    // };
    this.newVariableData = blankField();
    this.addVariableButtonFlag = true;
    // this.newVariableUniqueFlag = true;
    this.addVariableFlag = !this.addVariableFlag;
  }

  variableNameChanged() {
    // Was (2026-10-02, ADR 0009):
    // this.newVariableData.name = this.newVariableData.name.replace(/[^a-zA-Z_]/g, '');
    // this.newVariableData.name = this.newVariableData.name.replace(/system/gi, '');
    this.newVariableData.name = cleanFieldNameAsTyped(this.newVariableData.name);
  }

  newVariableTypeChanged() {
    // Was (2026-10-02, ADR 0009 — applyTypeDefaults owns this):
    // this.addVariableButtonFlag = true;
    // this.newVariableData.data = [];
    // if (this.newVariableData.type !== 'ARRAY_OF_OBJECT') {
    //   this.newVariableData.extendedAttributes = false;
    // }
    // switch (this.newVariableData.type) {
    //   case 'STRING':
    //     this.newVariableData.defaultValue = '';
    //     // this.newVariableUniqueFlag = true;
    //     break;
    //   case 'NUMBER':
    //     this.newVariableData.defaultValue = 0;
    //     // this.newVariableUniqueFlag = true;
    //     break;
    //   case 'BOOLEAN':
    //     // this.newVariableUniqueFlag = false;
    //     this.newVariableData.defaultValue = false;
    //     break;
    //   case 'AUTO_INCREMENT':
    //     this.newVariableData.startIncrementFrom = 0;
    //     this.newVariableData.defaultValue = 0;
    //     // this.newVariableUniqueFlag = false;
    //     break;
    //   case 'TIMESTAMP':
    //     // this.newVariableUniqueFlag = false;
    //     this.newVariableData.defaultMode = 'now'; // new TimeStamp fields default to live "now" per record
    //     this.newVariableData.defaultValue = '';   // used only when mode = 'fixed'
    //     // Superseded 2026-09-16 (kept per no-delete): this froze the default at field-creation time, so
    //     // every new record got the same fixed moment —
    //     // this.newVariableData.defaultValue = new Date().toISOString();
    //     break;
    //   case 'GEOMETRY':
    //     // Never unique: a jsonb geometry has no meaningful uniqueness constraint.
    //     // this.newVariableUniqueFlag = false;
    //     this.newVariableData.defaultValue = { type: 'Polygon', coordinates: [] };
    //     break;
    //   case 'UUID':
    //   case 'ATTACHMENT':
    //     // this.newVariableUniqueFlag = false;
    //     this.newVariableData.primaryKey = false;
    //     this.newVariableData.defaultValue = '';
    //     break;
    //   case 'ENTITY_REFERENCE':
    //     // this.newVariableUniqueFlag = false;
    //     this.addVariableButtonFlag = false;
    //     this.newVariableData.referenceEntityName = '';
    //     this.newVariableData.referenceEntityColumnName = 'id';
    //     break;
    //   case 'ARRAY_OF_OBJECT':
    //     // this.newVariableUniqueFlag = false;
    //     break;
    //   default:
    //     // this.newVariableUniqueFlag = true;
    // }
    // Object.assign, not a reassignment: the form's bindings keep the same object, exactly as before.
    const defaults = applyTypeDefaults(this.newVariableData);
    Object.assign(this.newVariableData, defaults.field);
    this.addVariableButtonFlag = defaults.addButtonEnabled;
  }

  newVariableReferenceEntityChanged() {
    if (this.newVariableData.referenceEntityName !== '') {
      this.addVariableButtonFlag = true;
    }
  }

  // Superseded 2026-10-02 by checkNewFieldName (zs-entity rules util) — no callers; kept per comment-don't-delete.
  uniqueVariableNameCheck(): boolean {
    if (!this.newVariableData.name) return false;
    const data = this.getTargetDataArray();
    return !data.some(d => d.name === this.newVariableData.name);
  }

  addVariable() {
    // Was (2026-10-02, ADR 0009 — same checks, same order, same words, now in checkNewFieldName):
    // this.newVariableData.name = this.newVariableData.name.replace(/[^a-zA-Z]/g, '');
    // if (this.newVariableData.name.toLowerCase().includes('system')) {
    //   alert('Variable name cannot contain the word "system"');
    //   return;
    // }
    // if (isReservedFieldName(this.newVariableData.name)) {
    //   alert(reservedFieldNameMessage(this.newVariableData.name));
    //   return;
    // }
    // this.newVariableData.id = this.newVariableData.name + '_' + this.generateTableId();
    // if (!this.uniqueVariableNameCheck()) {
    //   alert('Same variable name already exists, please change');
    //   return;
    // }
    this.newVariableData.name = cleanFieldNameOnAdd(this.newVariableData.name); // ⚠ N1: drops '_'
    const nameCheck = checkNewFieldName(this.newVariableData.name, this.getTargetDataArray());
    if (!nameCheck.ok) {
      alert(nameCheck.reason);
      return;
    }
    this.newVariableData.id = this.newVariableData.name + '_' + this.generateTableId();
    if (this.newVariableData.type === 'ARRAY_OF_OBJECT') {
      this.newVariableData.extendedAttributes = !!this.newVariableData.extendedAttributes;
      // parent_id references the container we're adding into: a nested AOB points at its parent
      // AOB, a top-level AOB at the entity (mirrors the recursive editor's behaviour).
      const parentRefId = this.resolveContainerField(this.addFieldTarget.path)?.id || this.newData.id;
      // Was (2026-10-02, ADR 0009 — field-builders owns these shapes):
      // this.newVariableData.data = [
      //   { name: 'id', type: 'UUID', isUnique: true, primaryKey: true, startIncrementFrom: 0, referenceEntityName: '', referenceEntityColumnName: 'id', id: '', data: [], defaultValue: '' },
      //   { name: 'id_auto_increment', type: 'AUTO_INCREMENT', isUnique: true, primaryKey: false, startIncrementFrom: 0, referenceEntityName: '', referenceEntityColumnName: 'id', id: '', data: [], defaultValue: 0 },
      //   { name: 'parent_id', type: 'ENTITY_REFERENCE', isUnique: true, primaryKey: false, startIncrementFrom: 0, referenceEntityName: parentRefId, referenceEntityColumnName: 'id', id: '', data: [], defaultValue: '' },
      // ];
      // if (this.newVariableData.extendedAttributes) {
      //   this.newVariableData.data.push(
      //     { name: 'systemKey', type: 'STRING', isUnique: false, primaryKey: false, startIncrementFrom: 0, referenceEntityName: '', referenceEntityColumnName: 'id', id: '', data: [], defaultValue: '' },
      //     { name: 'systemType', type: 'STRING', isUnique: false, primaryKey: false, startIncrementFrom: 0, referenceEntityName: '', referenceEntityColumnName: 'id', id: '', data: [], defaultValue: '' },
      //     { name: 'systemString', type: 'STRING', isUnique: false, primaryKey: false, startIncrementFrom: 0, referenceEntityName: '', referenceEntityColumnName: 'id', id: '', data: [], defaultValue: '' },
      //     { name: 'systemNumber', type: 'NUMBER', isUnique: false, primaryKey: false, startIncrementFrom: 0, referenceEntityName: '', referenceEntityColumnName: 'id', id: '', data: [], defaultValue: 0 },
      //     { name: 'systemBoolean', type: 'BOOLEAN', isUnique: false, primaryKey: false, startIncrementFrom: 0, referenceEntityName: '', referenceEntityColumnName: 'id', id: '', data: [], defaultValue: false },
      //     { name: 'systemTimestamp', type: 'TIMESTAMP', isUnique: false, primaryKey: false, startIncrementFrom: 0, referenceEntityName: '', referenceEntityColumnName: 'id', id: '', data: [], defaultValue: new Date().toISOString() },
      //   );
      // }
      // Was (2026-10-04, plan 4c Q41 — one shared builder for Add, Edit and the chat):
      // this.newVariableData.data = aobSystemFields(parentRefId);
      // if (this.newVariableData.extendedAttributes) {
      //   this.newVariableData.data.push(...extendedAttributeFields(new Date().toISOString()));
      // }
      this.newVariableData.data = listFields(parentRefId, this.newVariableData.extendedAttributes, new Date().toISOString());
    }
    this.getTargetDataArray().push(JSON.parse(JSON.stringify(this.newVariableData)));
    this.addVariableFlag = false;
    this.rebuildDetailGraph();
  }

  editVariableAt(index: number, target?: { path: number[] }) {
    if (target) this.addFieldTarget = target;
    const data = this.getTargetDataArray();
    this.editingVariableIndex = index;
    this.newVariableData = JSON.parse(JSON.stringify(data[index]));
    // Was (until 2026-10-10): a TimeStamp field with no stored mode was given 'now' here, so merely opening
    // and saving it wrote 'now' (and a CURRENT_TIMESTAMP database default). Why: owner rule (zavo_bug_oct_15) —
    // an absent mode stays absent unless the builder picks one; the select shows it as 'unset' (see timestampModeChoice).
    // if (this.newVariableData.type === 'TIMESTAMP' && !this.newVariableData.defaultMode) {
    //   this.newVariableData.defaultMode = 'now';
    // }
    // this.newVariableUniqueFlag = this.newVariableData.type === 'STRING' || this.newVariableData.type === 'NUMBER' || this.newVariableData.type === 'BOOLEAN';
    this.addVariableFlag = true;
    this.addVariableButtonFlag = true;
  }

  updateVariable() {
    if (this.editingVariableIndex === null) return;
    const data = this.getTargetDataArray();
    const currentVar = data[this.editingVariableIndex];
    if (this.detailEditFlag) {
      // Was (2026-10-02, ADR 0009 — same checks, same order, same words, now in checkRenamedFieldName):
      // this.newVariableData.name = this.newVariableData.name.replace(/[^a-zA-Z_]/g, '');
      // if (this.newVariableData.name.toLowerCase().includes('system')) {
      //   alert('Variable name cannot contain the word "system"');
      //   return;
      // }
      // if (this.newVariableData.name !== currentVar.name) {
      //   if (isReservedFieldName(this.newVariableData.name)) {
      //     alert(reservedFieldNameMessage(this.newVariableData.name));
      //     return;
      //   }
      //   const nameExists = data.some((item, idx) => item.name === this.newVariableData.name && idx !== this.editingVariableIndex);
      //   if (nameExists) { alert('A variable with this name already exists'); return; }
      // }
      this.newVariableData.name = cleanFieldNameOnEdit(this.newVariableData.name);
      const nameCheck = checkRenamedFieldName(this.newVariableData.name, currentVar.name, data, this.editingVariableIndex);
      if (!nameCheck.ok) {
        alert(nameCheck.reason);
        return;
      }
      // Plan 4c (Q41) — while New, a field turned INTO a list starts with the list's fields, as Add builds them
      // and as the entity chat does (parent_id → this list's container: the enclosing list, else the entity).
      if (this.newVariableData.type === 'ARRAY_OF_OBJECT' && currentVar.type !== 'ARRAY_OF_OBJECT') {
        this.newVariableData.extendedAttributes = !!this.newVariableData.extendedAttributes;
        const parentRefId = this.resolveContainerField(this.addFieldTarget.path)?.id || this.newData.id;
        this.newVariableData.data = listFields(parentRefId, this.newVariableData.extendedAttributes, new Date().toISOString());
      }
      data[this.editingVariableIndex] = JSON.parse(JSON.stringify(this.newVariableData));
    } else {
      // Was (2026-10-02, final review — the util owns which settings a locked field may change, N8):
      // currentVar.defaultValue = this.newVariableData.defaultValue;
      // currentVar.description = this.newVariableData.description;
      // Was (2026-10-03, N8 fixed — owner Q35: a finalized Timestamp's default mode was silently dropped):
      // for (const key of LOCKED_FIELD_EDITABLE_SETTINGS) {
      //   (currentVar as any)[key] = (this.newVariableData as any)[key];
      // }
      // Was (final review, 2026-10-03 — N4: the Foblex graph still lets a builder open a system timestamp,
      // and since N8 a mode change there was saved; the util now needs the field's name too):
      // for (const key of lockedFieldEditableSettings(currentVar.type)) {
      for (const key of lockedFieldEditableSettings(currentVar)) {
        (currentVar as any)[key] = (this.newVariableData as any)[key];
      }
    }
    this.editingVariableIndex = null;
    this.addVariableFlag = false;
    this.rebuildDetailGraph();
  }

  deleteVariableAt(index: number, target?: { path: number[] }) {
    if (target) this.addFieldTarget = target;
    const data = this.getTargetDataArray();
    const field = data[index];
    if (!field) return;
    // Was (2026-10-02, ADR 0009): if (field.name === 'id' || field.name === 'id_auto_increment' || field.name === 'parent_id') return;
    if (isProtectedField(field.name)) return;
    data.splice(index, 1);
    this.rebuildDetailGraph();
  }

  getEntityNameById(entityId: string): string {
    if (!entityId) return entityId;
    const entity = this.entityService.completeFinalizedEntityList.find(e => e.id === entityId);
    return entity ? entity.name : entityId;
  }

  getTargetEntityName(): string {
    if (!this.newVariableData.referenceEntityName) return 'target entity';
    const target = this.entityService.completeFinalizedEntityList.find(e => e.id === this.newVariableData.referenceEntityName);
    return target ? target.name : 'target entity';
  }

  tableNameChanged() {
    // Was (2026-10-02, ADR 0009):
    // this.newData.name = this.newData.name.replace(/[^a-zA-Z0-9]/g, '');
    // this.newData.name = this.newData.name.replace(/system/gi, '');
    // this.newData.name = this.newData.name.replace(/ootb/gi, '');
    this.newData.name = cleanEntityNameAsTyped(this.newData.name);
  }

  // Superseded 2026-10-03 by entityNameTaken (zs-entity rules util, N3) — no callers; kept per comment-don't-delete.
  private uniqueTableNameCheck(): boolean {
    if (!this.newData.name) return false;
    return !(this.entityList ?? []).some(e => e.id === this.newData.id);
  }

  private dowWithLoadingScreen = async (expression: Function) => {
    try {
      this.loading.isLoading = true;
      await expression();
    } finally {
      this.loading.isLoading = false;
    }
  }

  // Was (2026-10-30): async SaveTableChanges(finalizeFlag: boolean = false) {
  async SaveTableChanges(finalizeFlag: boolean = false, pressed: string = 'Save') {
    let flag = true;
    this.saveFailure = '';
    this.confirmWaits.saving(ENTITY_COMMIT);
    const mintedHere = !this.newData.id;
    if (!this.newData.id) {
      // Was (2026-10-02, ADR 0009 — same checks and words, now in checkEntityNameOnSave):
      // this.newData.name = this.newData.name.replace(/[^a-zA-Z0-9_]/g, '');
      // if (!this.newData.name.trim()) {
      //   this.saveNameError = 'Entity name is required';
      //   return false;
      // }
      // if (this.newData.name.toLowerCase().includes('system')) {
      //   this.saveNameError = 'Name cannot contain "system"';
      //   return false;
      // }
      this.newData.name = cleanEntityNameOnSave(this.newData.name);
      const nameCheck = checkEntityNameOnSave(this.newData.name);
      if (!nameCheck.ok) {
        this.saveNameError = nameCheck.reason;
        this.confirmWaits.report(ENTITY_COMMIT, { outcome: 'failed', reason: nameCheck.reason });
        return false;
      }
      this.saveNameError = '';
      // Was (2026-10-03, N3 fixed — owner Q34. The old check compared a freshly minted id against existing ids,
      // so it could never fail; and the id was minted BEFORE checking, so a refused Save would have left an id
      // behind and the next click would have skipped every check):
      // this.newData.id = this.newData.name + '_' + this.generateTableId();
      // flag = this.uniqueTableNameCheck();
      if (entityNameTaken(this.newData.name, this.entityList ?? [])) {
        alert(ENTITY_NAME_TAKEN_MESSAGE);
        this.confirmWaits.report(ENTITY_COMMIT, { outcome: 'failed', reason: ENTITY_NAME_TAKEN_MESSAGE });
        return false;
      }
      this.newData.id = this.newData.name + '_' + this.generateTableId();
    }
    for (const field of this.newData.data) {
      if (field.type === 'ARRAY_OF_OBJECT') {
        const parentIdField = field.data?.find((f: any) => f.name === 'parent_id');
        if (parentIdField) parentIdField.referenceEntityName = this.newData.id;
      }
    }
    // Since 2026-10-03 (N3) `flag` can no longer be false: the name is checked above, before the id is minted.
    // Was (final review, 2026-10-03 — the words live in ENTITY_NAME_TAKEN_MESSAGE now, not repeated here):
    // if (!flag) {
    //   alert('Same Data Model name already exists, please change');
    //   return false;
    // }
    if (!flag) {
      alert(ENTITY_NAME_TAKEN_MESSAGE);
      return false;
    }
    try {
      await this.dowWithLoadingScreen(async () => {
        await this.entityService.create(this.newData as Entity);
      });
      for (let i = 0; i < (this.entityList ?? []).length; i++) {
        if (this.entityList![i].id === this.newData.id) {
          this.entityList![i] = JSON.parse(JSON.stringify(this.newData));
          break;
        }
      }
      if (!finalizeFlag) {
        this.confirmWaits.report(ENTITY_COMMIT, { outcome: 'saved', saved: JSON.parse(JSON.stringify(this.newData)), pressed });
        this.exitEntity();
      }
    } catch (error) {
      console.error('Error saving entity:', error);
      // Was (2026-10-30): the catch fell through to `return flag`, true after a failed save.
      // A failed first save must not keep its id either, or the retry skips the name checks and the name input is gone.
      if (mintedHere) this.newData.id = '';
      this.saveFailed(error);
      return false;
    }
    return flag;
  }

  /** Says why on the page, and tells a waiting Claude only when the wait ends: the builder can fix it and press again. */
  private saveFailed(error: unknown): void {
    this.saveFailure = (error as Error)?.message || 'The save failed. Please try again.';
    this.confirmWaits.report(ENTITY_COMMIT, { outcome: 'failed', reason: this.saveFailure });
  }

  // Was (2026-10-30): no catch, so a failed Update reached nobody.
  async UpdateTableChanges() {
    this.saveFailure = '';
    this.confirmWaits.saving(ENTITY_COMMIT);
    try {
      await this.dowWithLoadingScreen(async () => {
        await this.entityService.create({ ...this.newData, updatedBySystem: false } as Entity);
      });
    } catch (error) {
      console.error('Error updating entity:', error);
      this.saveFailed(error);
      return false;
    }
    this.confirmWaits.report(ENTITY_COMMIT, { outcome: 'saved', saved: JSON.parse(JSON.stringify(this.newData)), pressed: 'Update' });
    this.exitEntity();
    return true;
  }

  async FinalizeTableChanges() {
    const flag = await this.SaveTableChanges(true);
    if (flag) {
      // Pending, not Ready: the table is built by a queue, and only Zavo marks it Ready once it
      // exists (glossary: Pending, Ready; functions ADR 0005).
      const before = this.newData.status;
      this.newData.status = 'PENDING';
      // A failed second save stays NEW, or the retry would have lost its Finalize button.
      if (!(await this.SaveTableChanges(false, 'Finalize'))) this.newData.status = before;
    }
  }

  async LocalTableChanges() {
    const flag = await this.SaveTableChanges(true);
    if (flag) {
      this.newData.status = 'LOCAL';
      await this.SaveTableChanges(false, 'Local');
    }
  }

  private rebuildDetailGraph() {
    if (this.detailGraph) {
      this.buildDetailGraph();
    }
  }

  private removeDetailEventListener() {
    if (this.detailEventListener) {
      document.removeEventListener('detail-field-action', this.detailEventListener);
      this.detailEventListener = null;
    }
  }

  private setupDetailEventListener() {
    this.removeDetailEventListener();
    this.detailEventListener = (e: Event) => {
      const d = (e as CustomEvent).detail;
      if (!d) return;
      const path: number[] = Array.isArray(d.path) ? d.path : [];
      const target = { path };
      if (d.action === 'add') {
        this.addVariableChangeFlag(target);
      } else if (d.action === 'edit') {
        this.editVariableAt(d.fieldIndex, target);
      } else if (d.action === 'delete') {
        const data = this.resolveContainerData(path);
        if (confirm(`Delete field "${data[d.fieldIndex]?.name}"?`)) {
          this.deleteVariableAt(d.fieldIndex, target);
        }
      }
    };
    document.addEventListener('detail-field-action', this.detailEventListener);
  }

  private static detailShapeRegistered = false;
  private static registerDetailShapes() {
    if (EntityListComponent.detailShapeRegistered) return;

    const typeColor: Record<string, { bg: string; fg: string }> = {
      STRING:           { bg: 'rgba(34,197,94,0.12)',  fg: '#4ade80' },
      NUMBER:           { bg: 'rgba(59,130,246,0.12)', fg: '#60a5fa' },
      UUID:             { bg: 'rgba(168,85,247,0.12)', fg: '#c084fc' },
      BOOLEAN:          { bg: 'rgba(234,179,8,0.12)',  fg: '#facc15' },
      ENTITY_REFERENCE: { bg: 'rgba(249,115,22,0.12)', fg: '#fb923c' },
      TIMESTAMP:        { bg: 'rgba(6,182,212,0.12)',  fg: '#22d3ee' },
      JSONB:            { bg: 'rgba(236,72,153,0.12)', fg: '#f472b6' },
      ID_AUTO_INCREMENT:{ bg: 'rgba(139,92,246,0.12)', fg: '#a78bfa' },
      AUTO_INCREMENT:   { bg: 'rgba(139,92,246,0.12)', fg: '#a78bfa' },
      ATTACHMENT:        { bg: 'rgba(148,163,184,0.12)', fg: '#94a3b8' },
    };
    const defaultTC = { bg: 'rgba(148,163,184,0.12)', fg: '#94a3b8' };

    const buildFieldRows = (fields: any[], container: HTMLElement, path: number[], editFlag: boolean) => {
      for (const f of fields) {
        const tc = typeColor[f.type] || defaultTC;
        const refLabel = f.type === 'ENTITY_REFERENCE' && f.ref ? ` → ${f.ref}` : '';
        // Read-only in the schema view (shown, but no edit/delete): the id/parent trio AND any
        // "system"-word field (name contains "system") — those are system-managed. See project_system_fields_rule.
        // Was (2026-10-02, ADR 0009): const isSystem = f.name === 'id' || f.name === 'id_auto_increment' || f.name === 'parent_id' || isSystemVariableKey(f.name);
        const isSystem = isSystemField(f.name); // ⚠ N4: the Foblex graph's SYSTEM_FIELDS is narrower
        const row = document.createElement('div');
        row.dataset['mcpField'] = f.name;
        row.style.cssText = 'display:flex;align-items:center;justify-content:space-between;padding:5px 4px;border-bottom:1px solid rgba(255,255,255,0.04);cursor:pointer;transition:background 0.15s;border-radius:4px;';
        // Was (2026-10-03, final review — C1, the rule moves into the util the entity chat asks too):
        // const canEdit = !isSystem && f.type !== 'ARRAY_OF_OBJECT';
        const canEdit = canOpenFieldForEdit(f);
        row.innerHTML = `
          <span style="font-size:11px;color:#e2e8f0;font-weight:500">${f.name}</span>
          <div style="display:flex;align-items:center;gap:6px;flex-shrink:0;margin-left:8px">
            <span style="font-size:9px;padding:1px 6px;border-radius:3px;background:${tc.bg};color:${tc.fg};font-weight:600;white-space:nowrap">${f.type}${refLabel}</span>
            ${editFlag && !isSystem ? `<span class="field-del-btn" style="opacity:0;transition:opacity 0.15s;cursor:pointer;font-size:10px;color:#f87171;padding:2px 4px;border-radius:3px;background:rgba(248,113,113,0.1)">✕</span>` : ''}
          </div>
        `;
        row.addEventListener('mouseenter', () => { row.style.background = 'rgba(255,255,255,0.04)'; row.querySelectorAll('.field-del-btn').forEach((el: any) => el.style.opacity = '1'); });
        row.addEventListener('mouseleave', () => { row.style.background = 'transparent'; row.querySelectorAll('.field-del-btn').forEach((el: any) => el.style.opacity = '0'); });
        const payload = { path, fieldIndex: f.fieldIndex };
        if (canEdit) row.addEventListener('click', (e: Event) => { document.dispatchEvent(new CustomEvent('detail-field-action', { detail: { action: 'edit', ...payload } })); });
        const delBtn = row.querySelector('.field-del-btn');
        if (delBtn) delBtn.addEventListener('click', (e: Event) => { e.stopPropagation(); document.dispatchEvent(new CustomEvent('detail-field-action', { detail: { action: 'delete', ...payload } })); });
        container.appendChild(row);
      }
    };

    const buildAddButton = (accentColor: string, path: number[], status: string) => {
      const btn = document.createElement('div');
      if (status === 'OOTB') {
        btn.style.cssText = 'display:none';
        return btn;
      }
      btn.style.cssText = `flex-shrink:0;padding:8px 16px;border-top:1px solid rgba(255,255,255,0.06);display:flex;align-items:center;justify-content:center;cursor:pointer;transition:background 0.15s;gap:6px;`;
      btn.innerHTML = `<span style="font-size:14px;color:${accentColor};font-weight:700;line-height:1">+</span><span style="font-size:10px;color:${accentColor};font-weight:600">Add Field</span>`;
      btn.addEventListener('mouseenter', () => { btn.style.background = 'rgba(255,255,255,0.04)'; });
      btn.addEventListener('mouseleave', () => { btn.style.background = 'transparent'; });
      btn.addEventListener('click', (e: Event) => {
        e.stopPropagation();
        document.dispatchEvent(new CustomEvent('detail-field-action', { detail: { action: 'add', path } }));
      });
      return btn;
    };

    Shape.HTML.register({
      shape: 'entity-detail-card',
      effect: ['data'],
      html(cell: any) {
        const d = cell.getData() || {};
        const fields: any[] = d.fields || [];
        const wrap = document.createElement('div');
        wrap.style.cssText = 'width:100%;height:100%;display:flex;flex-direction:column;overflow:hidden;background:rgba(30,30,40,0.95);border:1.5px solid rgba(168,85,247,0.4);border-radius:12px;font-family:system-ui,-apple-system,sans-serif;box-sizing:border-box;box-shadow:0 0 24px rgba(168,85,247,0.15);';

        const header = document.createElement('div');
        header.style.cssText = 'padding:12px 16px;border-bottom:1px solid rgba(255,255,255,0.08);flex-shrink:0;display:flex;align-items:center;justify-content:space-between';
        header.innerHTML = `
          <span style="font-size:14px;font-weight:700;color:#f3f4f6">${d.name || ''}</span>
          <span style="padding:2px 8px;border-radius:4px;font-size:9px;font-weight:600;letter-spacing:0.5px;text-transform:uppercase;background:${d.statusBg || 'transparent'};color:${d.statusFg || '#aaa'}">${d.status || ''}</span>
        `;

        const subHeader = document.createElement('div');
        subHeader.style.cssText = 'padding:4px 16px;flex-shrink:0';
        subHeader.innerHTML = `<span style="font-size:10px;color:rgba(255,255,255,0.35)">${fields.length} fields</span>`;

        const fieldsContainer = document.createElement('div');
        fieldsContainer.style.cssText = 'flex:1;overflow-y:auto;padding:0 16px 4px;scrollbar-width:thin;scrollbar-color:rgba(128,128,128,0.2) transparent';
        const path: number[] = d.path || [];
        buildFieldRows(fields, fieldsContainer, path, !!d.editFlag);

        wrap.appendChild(header);
        wrap.appendChild(subHeader);
        wrap.appendChild(fieldsContainer);
        wrap.appendChild(buildAddButton('#a78bfa', path, d.status || ''));
        return wrap;
      },
    });

    Shape.HTML.register({
      shape: 'aob-detail-card',
      effect: ['data'],
      html(cell: any) {
        const d = cell.getData() || {};
        const fields: any[] = d.fields || [];
        const path: number[] = d.path || [];
        const wrap = document.createElement('div');
        wrap.style.cssText = 'width:100%;height:100%;display:flex;flex-direction:column;overflow:hidden;background:rgba(30,30,40,0.95);border:1.5px solid rgba(249,115,22,0.4);border-radius:12px;font-family:system-ui,-apple-system,sans-serif;box-sizing:border-box;box-shadow:0 0 16px rgba(249,115,22,0.1);';

        const isAaob = d.isAaob ? '<span style="font-size:8px;padding:1px 5px;border-radius:3px;background:rgba(249,115,22,0.15);color:#fb923c;font-weight:600;margin-left:6px">AAOB</span>' : '';

        const header = document.createElement('div');
        header.style.cssText = 'padding:10px 14px;border-bottom:1px solid rgba(255,255,255,0.08);flex-shrink:0;display:flex;align-items:center';
        header.innerHTML = `
          <span style="font-size:9px;padding:1px 5px;border-radius:3px;background:rgba(249,115,22,0.15);color:#fb923c;font-weight:700;margin-right:8px">AOB</span>
          <span style="font-size:13px;font-weight:600;color:#f3f4f6">${d.name || ''}</span>
          ${isAaob}
        `;

        const subHeader = document.createElement('div');
        subHeader.style.cssText = 'padding:4px 14px;flex-shrink:0';
        subHeader.innerHTML = `<span style="font-size:10px;color:rgba(255,255,255,0.35)">${fields.length} fields</span>`;

        const fieldsContainer = document.createElement('div');
        fieldsContainer.style.cssText = 'flex:1;overflow-y:auto;padding:0 14px 4px;scrollbar-width:thin;scrollbar-color:rgba(128,128,128,0.2) transparent';
        buildFieldRows(fields, fieldsContainer, path, !!d.editFlag);

        wrap.appendChild(header);
        wrap.appendChild(subHeader);
        wrap.appendChild(fieldsContainer);
        wrap.appendChild(buildAddButton('#fb923c', path, d.status || ''));
        return wrap;
      },
    });

    EntityListComponent.detailShapeRegistered = true;
  }

  private initDetailGraph() {
    if (!this.detailContainerRef) return;
    EntityListComponent.registerDetailShapes();
    this.setupDetailEventListener();

    if (this.detailGraph) { this.detailGraph.dispose(); this.detailGraph = null; }

    const container = this.detailContainerRef.nativeElement as HTMLElement;
    this.detailGraph = new Graph({
      container,
      ...X6_SYNC_RENDER,
      autoResize: true,
      background: { color: 'transparent' },
      panning: { enabled: true, eventTypes: ['leftMouseDown', 'rightMouseDown'] },
      mousewheel: { enabled: false },
      scaling: { min: 0.3, max: 2 },
      interacting: { nodeMovable: false },
    });

    this.buildDetailGraph();
  }

  private buildDetailGraph() {
    const graph = this.detailGraph;
    if (!graph || !this.newData) return;
    graph.clearCells();

    const entity = this.newData;
    const sc = this.getStatusColors(entity.status);
    const rowH = 26;
    const headerH = 80;
    const addBtnH = 36;
    const calcCardWidth = (fields: { name: string; type: string; ref?: string }[], min: number) => {
      let longest = 0;
      for (const f of fields) {
        const typeLabel = f.type + (f.type === 'ENTITY_REFERENCE' && f.ref ? ` → ${f.ref}` : '');
        const est = f.name.length * 7 + typeLabel.length * 6 + 100;
        if (est > longest) longest = est;
      }
      return Math.max(min, longest);
    };

    const portAttrs = { circle: { r: 5, magnet: true, stroke: 'rgba(249,115,22,0.7)', fill: 'rgba(249,115,22,0.4)', strokeWidth: 2 } };
    const nodeMeta: { id: string; width: number; height: number }[] = [];
    const pendingEdges: { sourceId: string; sourcePort: string; targetId: string; targetPort: string }[] = [];

    // Recursively render a container (the entity root or any AOB, at any depth) as a node, and
    // each ARRAY_OF_OBJECT child as its own connected node — so nested AOBs are openable too.
    const addContainerNode = (container: Entity | TypeData, path: number[], isRoot: boolean): string => {
      const allFields: TypeData[] = ((container as any).data as TypeData[]) || [];
      const scalarFields: { name: string; type: string; ref: string; fieldIndex: number }[] = [];
      const aobChildren: { field: TypeData; idx: number }[] = [];
      allFields.forEach((f, idx) => {
        if (f.type === 'ARRAY_OF_OBJECT') aobChildren.push({ field: f, idx });
        else scalarFields.push({ name: f.name, type: f.type, ref: f.referenceEntityName || '', fieldIndex: idx });
      });

      const nodeId = isRoot ? 'main' : `aob_${path.join('_')}`;
      const maxH = isRoot ? window.innerHeight - 160 : 400;
      const height = Math.min(headerH + scalarFields.length * rowH + addBtnH, maxH);
      const width = calcCardWidth(scalarFields, isRoot ? 340 : 300);

      const portItems: any[] = [];
      if (!isRoot) portItems.push({ id: `${nodeId}__in`, group: 'in' });
      aobChildren.forEach((_, i) => portItems.push({ id: `${nodeId}__out_${i}`, group: 'out' }));

      graph.addNode({
        id: nodeId,
        x: 0, y: 0,
        width, height,
        shape: isRoot ? 'entity-detail-card' : 'aob-detail-card',
        data: isRoot
          ? { name: entity.name, status: entity.status, statusBg: sc.bg, statusFg: sc.fg, fields: scalarFields, editFlag: this.detailEditFlag, path }
          : { name: (container as TypeData).name, fields: scalarFields, isAaob: !!(container as TypeData).extendedAttributes, editFlag: this.detailEditFlag, status: entity.status, path },
        ports: {
          groups: {
            in:  { position: 'left',  attrs: portAttrs },
            out: { position: 'right', attrs: portAttrs },
          },
          items: portItems,
        },
      });
      nodeMeta.push({ id: nodeId, width, height });

      aobChildren.forEach((child, i) => {
        const childId = addContainerNode(child.field, [...path, child.idx], false);
        pendingEdges.push({ sourceId: nodeId, sourcePort: `${nodeId}__out_${i}`, targetId: childId, targetPort: `${childId}__in` });
      });

      return nodeId;
    };

    addContainerNode(entity, [], true);

    for (const e of pendingEdges) {
      graph.addEdge({
        source: { cell: e.sourceId, port: e.sourcePort },
        target: { cell: e.targetId, port: e.targetPort },
        attrs: { line: { stroke: 'rgba(249,115,22,0.5)', strokeWidth: 2, targetMarker: { name: 'classic', size: 8 } } },
      });
    }

    // Tree layout (left → right) via dagre, mirroring the overview graph.
    const g = new dagre.graphlib.Graph();
    g.setGraph({ rankdir: 'LR', nodesep: 30, ranksep: 80 });
    g.setDefaultEdgeLabel(() => ({}));
    for (const n of nodeMeta) g.setNode(n.id, { width: n.width, height: n.height });
    for (const e of pendingEdges) g.setEdge(e.sourceId, e.targetId);
    dagre.layout(g);
    for (const n of nodeMeta) {
      const pos = g.node(n.id);
      const cell = graph.getCellById(n.id) as any;
      if (pos && cell) cell.setPosition(pos.x - n.width / 2, pos.y - n.height / 2);
    }

    graph.zoomToFit({ padding: 60, maxScale: 1 });
  }

  generateTableId(): string {
    const randomInt = Math.floor(Math.random() * 10000000000000);
    const timestamp = Date.now();
    const combinedValue = randomInt + timestamp; // Combine random number and timestamp
    const base64String = btoa(String(combinedValue));
    return base64String.substring(0, 6); // Extract first 6 characters
  }

  async addOOTBEntity() {
    if (!this.selectedEntity) {
      return;
    }
    var newEntity = {
      ...this.ootb.find((ele) => ele.id === this.selectedEntity),
      // OOTB entities ship with finalized schemas — skip the NEW (draft)
      // state and land them finalised directly, without an intermediate
      // finalize step. PENDING until the table queue builds them; only Zavo
      // marks them READY (functions ADR 0005).
      status: 'PENDING',
    };
    // Inject top-level id and id_auto_increment if missing
    if (!newEntity.data?.find((ele) => ele.name === 'id_auto_increment')) {
      newEntity.data?.unshift({
        name: 'id_auto_increment',
        type: 'AUTO_INCREMENT',
        isUnique: true,
        primaryKey: false,
        startIncrementFrom: 0,
        referenceEntityName: '',
        referenceEntityColumnName: 'id',
        id: '',
        data: [],
        defaultValue: 0,
      });
    }
    if (!newEntity.data?.find((ele) => ele.name === 'id')) {
      newEntity.data?.unshift({
        name: 'id',
        type: 'UUID',
        isUnique: true,
        primaryKey: true,
        startIncrementFrom: 0,
        referenceEntityName: '',
        referenceEntityColumnName: 'id',
        id: '',
        data: [],
        defaultValue: '',
      });
    }
    // Inject id, id_auto_increment, parent_id into every ARRAY_OF_OBJECT child
    if (newEntity.data?.length) {
      newEntity.data = this.injectChildSystemFields(newEntity.data, newEntity.id!);
    }
    await this.entityService.create(newEntity as Entity);
    await this.entityService.query(1000);
    this.selectedEntity = '';
  }

  injectChildSystemFields(data: TypeData[], parentEntityId: string): TypeData[] {
    return data.map((field) => {
      if (field.type !== 'ARRAY_OF_OBJECT') return field;
      const childData = [...(field.data || [])];
      if (!childData.find((f) => f.name === 'parent_id')) {
        childData.unshift({
          name: 'parent_id',
          type: 'ENTITY_REFERENCE',
          isUnique: false,
          primaryKey: false,
          startIncrementFrom: 0,
          referenceEntityName: parentEntityId,
          referenceEntityColumnName: 'id',
          id: '',
          data: [],
          defaultValue: '',
        });
      }
      if (!childData.find((f) => f.name === 'id_auto_increment')) {
        childData.unshift({
          name: 'id_auto_increment',
          type: 'AUTO_INCREMENT',
          isUnique: true,
          primaryKey: false,
          startIncrementFrom: 0,
          referenceEntityName: '',
          referenceEntityColumnName: 'id',
          id: '',
          data: [],
          defaultValue: 0,
        });
      }
      if (!childData.find((f) => f.name === 'id')) {
        childData.unshift({
          name: 'id',
          type: 'UUID',
          isUnique: true,
          primaryKey: true,
          startIncrementFrom: 0,
          referenceEntityName: '',
          referenceEntityColumnName: 'id',
          id: '',
          data: [],
          defaultValue: '',
        });
      }
      return { ...field, data: childData };
    });
  }

  // Update the filtering method to handle 'new' filter
  applyFilter() {
    if (!this.entityList) {
      this.filteredEntityList = undefined;
      return;
    }

    // Hidden system/control entities (systemOotbTenant / systemOotbTenantInvite) are never shown in the
    // management list — they still exist + sync + back the identity blocks, just out of the builder's face.
    const visible = this.entityList.filter((entity) => !entity.hidden);

    switch (this.activeFilter) {
      case 'local':
        this.filteredEntityList = visible.filter(
          (entity) => entity.status === 'LOCAL',
        );
        break;
      case 'finalized':
        // Was (2026-10-02, final review — isFinalized decides, same four statuses):
        // this.filteredEntityList = visible.filter(
        //   (entity) => entity.status === 'READY' || entity.status === 'PENDING' || entity.status === 'SYNCED' || entity.status === 'ERROR',
        // );
        this.filteredEntityList = visible.filter(
          (entity) => isFinalized(entity.status),
        );
        break;
      case 'new':
        this.filteredEntityList = visible.filter(
          (entity) => entity.status === 'NEW',
        );
        break;
      case 'all':
      default:
        this.filteredEntityList = visible;
        break;
    }
  }

  // Method to change the filter
  setFilter(filter: string) {
    this.activeFilter = (filter as any);
    this.applyFilter();
  }

  // Easter egg (local-only, resets on reload):
  // 1) Double-tap the title
  // 2) Long-press the subtitle (~800ms)
  // 3) Tap the title once
  secretToOpenOOTBEntityFlag = false;
  ootbSecretStage: 'idle' | 'armed' | 'ready' = 'idle';
  ootbHeaderPulse = false;
  ootbIconSpin = false;

  private ootbTitleLastTapAt = 0;
  private ootbLongPressTimer: ReturnType<typeof setTimeout> | null = null;
  private ootbStageResetTimer: ReturnType<typeof setTimeout> | null = null;
  private ootbFinalTapDeadlineTimer: ReturnType<typeof setTimeout> | null = null;

  private triggerOotbPulse() {
    this.ootbHeaderPulse = false;
    // Next tick to re-trigger the animation class reliably
    setTimeout(() => (this.ootbHeaderPulse = true), 0);
    setTimeout(() => (this.ootbHeaderPulse = false), 260);
  }

  private triggerOotbSpin() {
    this.ootbIconSpin = false;
    setTimeout(() => (this.ootbIconSpin = true), 0);
    setTimeout(() => (this.ootbIconSpin = false), 650);
  }

  private clearOotbTimers() {
    if (this.ootbLongPressTimer) {
      clearTimeout(this.ootbLongPressTimer);
      this.ootbLongPressTimer = null;
    }
    if (this.ootbStageResetTimer) {
      clearTimeout(this.ootbStageResetTimer);
      this.ootbStageResetTimer = null;
    }
    if (this.ootbFinalTapDeadlineTimer) {
      clearTimeout(this.ootbFinalTapDeadlineTimer);
      this.ootbFinalTapDeadlineTimer = null;
    }
  }

  private resetOotbSecret() {
    this.clearOotbTimers();
    this.ootbSecretStage = 'idle';
    this.ootbTitleLastTapAt = 0;
  }

  onOotbTitleTap() {
    if (this.secretToOpenOOTBEntityFlag) return;

    const now = Date.now();

    // Final step: once ready, a single tap unlocks (within 2s)
    if (this.ootbSecretStage === 'ready') {
      this.secretToOpenOOTBEntityFlag = true;
      this.resetOotbSecret();
      this.triggerOotbPulse();
      this.triggerOotbSpin();
      return;
    }

    // First step: double-tap to arm
    if (now - this.ootbTitleLastTapAt <= 380) {
      this.ootbSecretStage = 'armed';
      this.triggerOotbPulse();
      this.triggerOotbSpin();

      // Give user a window to long-press subtitle
      this.clearOotbTimers();
      this.ootbStageResetTimer = setTimeout(() => this.resetOotbSecret(), 5000);
      this.ootbFinalTapDeadlineTimer = setTimeout(() => {
        // If they somehow reached ready without final tap, reset
        if (!this.secretToOpenOOTBEntityFlag) this.resetOotbSecret();
      }, 7000);
      this.ootbTitleLastTapAt = 0;
      return;
    }

    this.ootbTitleLastTapAt = now;

    // If user is already armed and taps title again, keep it playful (pulse), but don't unlock.
    if (this.ootbSecretStage === 'armed') {
      this.triggerOotbPulse();
    }
  }

  onOotbSubtitlePointerDown() {
    if (this.secretToOpenOOTBEntityFlag) return;
    if (this.ootbSecretStage !== 'armed') return;

    this.clearOotbTimers();
    // Long-press to move to ready stage
    this.ootbLongPressTimer = setTimeout(() => {
      this.ootbSecretStage = 'ready';
      this.triggerOotbPulse();
      this.triggerOotbSpin();

      // Need the final title tap soon, otherwise reset
      this.ootbFinalTapDeadlineTimer = setTimeout(() => this.resetOotbSecret(), 2000);
    }, 800);
  }

  onOotbSubtitlePointerUpOrLeave() {
    if (this.ootbLongPressTimer) {
      clearTimeout(this.ootbLongPressTimer);
      this.ootbLongPressTimer = null;
    }
  }
  // Navigation to OOTB Entity List
  navigateToOOTBEntity() {
    this.router.navigate(['/dashboard/ootb-entity']);
  }

  // Load global functions
  async loadGlobalFunctions() {
    const functions = await this.functionListGlobalService.query();
    this.globalFunctions = functions || [];
  }

  // Entities must exist AND be SYNCED before a function that references them can be imported.
  private isEntitySynced(entityId: string): boolean {
    const entity = this.entityList?.find(e => e.id === entityId);
    return !!entity && entity.status === 'SYNCED';
  }

  // Check if a function can be imported (all referenced entities exist and are SYNCED)
  canImportFunction(func: FunctionInterface): boolean {
    if (!this.entityList) return false;
    if (Array.isArray(func.requiredEntityReference) && func.requiredEntityReference.length > 0) {
      if (!func.requiredEntityReference.every(ref => this.isEntitySynced(ref))) return false;
    }
    const inputsValid = func.inputs?.every(input => !input.entityReference || this.isEntitySynced(input.entityReference)) ?? true;
    const outputsValid = func.outputs?.every(output => !output.entityReference || this.isEntitySynced(output.entityReference)) ?? true;
    return inputsValid && outputsValid;
  }

  // Get entity references that are missing entirely (not imported into this tenant)
  getMissingEntities(func: FunctionInterface): string[] {
    return this.collectReferencedEntities(func).filter(ref => !this.entityList?.some(e => e.id === ref));
  }

  // Get entity references that exist but are not SYNCED yet
  getUnsyncedEntities(func: FunctionInterface): { id: string; status: string }[] {
    return this.collectReferencedEntities(func)
      .map(ref => this.entityList?.find(e => e.id === ref))
      .filter((e): e is Entity => !!e && e.status !== 'SYNCED')
      .map(e => ({ id: e.id, status: e.status }));
  }

  private collectReferencedEntities(func: FunctionInterface): string[] {
    const refs = new Set<string>();
    (func.requiredEntityReference ?? []).forEach(ref => refs.add(ref));
    func.inputs?.forEach(input => { if (input.entityReference) refs.add(input.entityReference); });
    func.outputs?.forEach(output => { if (output.entityReference) refs.add(output.entityReference); });
    return Array.from(refs);
  }

  // Import selected global function
  async importGlobalFunction() {
    if (!this.selectedGlobalFunction) {
      return;
    }
    const functionToImport = this.globalFunctions.find(
      (func) => func.name === this.selectedGlobalFunction
    );
    if (functionToImport) {
      // Check if already imported locally
      if (this.isFunctionAlreadyImported(functionToImport)) {
        alert(`Function "${functionToImport.name}" is already imported.`);
        this.selectedGlobalFunction = '';
        return;
      }
      // Validate that all entity references exist and are SYNCED
      if (!this.canImportFunction(functionToImport)) {
        const missing = this.getMissingEntities(functionToImport);
        const unsynced = this.getUnsyncedEntities(functionToImport);
        const parts: string[] = [];
        if (missing.length) parts.push(`Missing entities: ${missing.join(', ')}`);
        if (unsynced.length) parts.push(`Not synced yet: ${unsynced.map(e => `${e.id} (${e.status})`).join(', ')}`);
        alert(`Cannot import function "${functionToImport.name}". ${parts.join(' | ')}`);
        return;
      }
      
      // Import logic here - could copy to local function list
      functionToImport.id = functionToImport.name; // Clear ID for new creation
      await this.functionListService.create(functionToImport);
      await this.functionListService.query();
      console.log('Importing function:', functionToImport);
      alert(`Function "${functionToImport.name}" imported successfully!`);
      this.selectedGlobalFunction = '';
    }
  }

  // Helper method to check if selected function can be imported
  canImportSelectedFunction(): boolean {
    if (!this.selectedGlobalFunction) return false;
    const func = this.globalFunctions.find(f => f.name === this.selectedGlobalFunction);
    return func ? this.canImportFunction(func) && !this.isFunctionAlreadyImported(func) : false;
  }

  // Check if a function is already present in local function list
  isFunctionAlreadyImported(func: FunctionInterface): boolean {
    return this.localFunctions?.some((f) => f.name === func.name) ?? false;
  }

  isSelectedFunctionImported(): boolean {
    if (!this.selectedGlobalFunction) return false;
    return this.localFunctions?.some((f) => f.name === this.selectedGlobalFunction) ?? false;
  }

  async runSyncEntities() {
    if (this.fixState === 'running') return;
    this.fixState = 'running';
    this.fixReport = null;
    this.fixError = null;
    this.showFixReport = false;
    this.confirmWaits.saving(ENTITY_SYNC_COMMIT);

    try {
      const token = await this.authService.getAuthToken();
      if (!token) throw new Error('Not authenticated');

      const res = await fetch(`${environment.functionsBaseUrl}/syncEntities`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ clientId: this.clientService.selectedClientId || undefined }),
      });

      // Read the body ONCE as text, then parse only if it's actually JSON. A crashed/killed
      // syncEntities (OOM / timeout / 500) returns a PLAINTEXT body like "Internal Server Error";
      // calling res.json() on that throws a confusing "Unexpected token 'I'… is not valid JSON",
      // hiding the real failure. Surface the HTTP status + body (with an OOM/timeout hint) instead.
      const rawBody = await res.text();
      let json: any = null;
      try { json = rawBody ? JSON.parse(rawBody) : null; } catch { /* non-JSON server error */ }

      if (!res.ok) {
        const detail = (json && json.error) || rawBody.trim() || res.statusText || 'Request failed';
        const hint = res.status >= 500
          ? ' — the sync service errored (likely ran out of memory or timed out). Retry; if it persists, the syncEntities function memory/timeout needs raising.'
          : '';
        throw new Error(`Sync failed (HTTP ${res.status}): ${detail}${hint}`);
      }
      if (!json) throw new Error('Sync returned an empty or non-JSON response.');

      this.fixReport = json.report as FixReport;
      this.fixState = 'done';
      // Reload entities to reflect the newly-created tables / status flips
      await this.entityService.query(1000);
      this.confirmWaits.report(ENTITY_SYNC_COMMIT, { outcome: 'saved', saved: this.fixReport });
    } catch (err: any) {
      this.fixError = err.message ?? 'Unknown error';
      this.fixState = 'error';
      this.confirmWaits.report(ENTITY_SYNC_COMMIT, { outcome: 'failed', reason: this.fixError! });
    }
  }

  get fixHasProblems(): boolean {
    if (!this.fixReport) return false;
    return (
      this.fixReport.failed.length > 0 ||
      this.fixReport.fkFailed.length > 0 ||
      this.fixReport.hasuraFailed.length > 0 ||
      this.fixReport.orphanedColumns.length > 0
    );
  }

  get featureComponentStatus() {
    return FEATURE_COMPONENTS.map(feat => ({
      def: feat,
      entityStatus: feat.requiredEntities.map(id => ({
        id,
        present: this.isOotbAlreadyImported(id),
      })),
      functionStatus: feat.requiredFunctions.map(name => ({
        name,
        present: this.localFunctions.some(f => f.name === name),
      })),
      allReady:
        feat.requiredEntities.every(id => this.isOotbAlreadyImported(id)) &&
        feat.requiredFunctions.every(name => this.localFunctions.some(f => f.name === name)),
    }));
  }

  ngOnInit() {
    this.entityPageBridge.register(this.chatPage, this.confirmPage);
    // The page holds the edits, so the page says what leaving would lose: Back, the left nav and the navigator all ask here.
    this.nav.setLeaveGuard(() => {
      const open = this.chatPage.open();
      return open ? whatLeavingLoses(this.chatPage.openSaved(), open) : null;
    });
    // Load roles for the entity Role-Permissions UI (same source as the old EntityComponent).
    this.roleService.query(1000);
    this.roleService.$values.subscribe((values) => {
      this.roles = values || [];
    });
  }

  ngAfterViewInit() {
    this.initX6Graph();
  }

  ngOnDestroy() {
    this.entityPageBridge.unregister();
    for (const commit of ENTITY_COMMITS) this.confirmWaits.report(commit, { outcome: 'closed', because: 'the builder left the screen' });
    this.nav.setLeaveGuard(null);
    this.destroyed = true;
    this.pendingStatus.stop();
    this.navIntentSub.unsubscribe();
    this.removeDetailEventListener();
    this.x6Graph?.dispose();
    this.detailGraph?.dispose();
  }

  @HostListener('window:resize')
  onResize() {
    if (this.x6Graph && this.x6ContainerRef) {
      const el = this.x6ContainerRef.nativeElement as HTMLElement;
      this.x6Graph.resize(el.clientWidth, el.clientHeight);
      this.x6Graph.zoomToFit({ padding: 40, maxScale: 1.2 });
    }
  }

  private static x6ShapeRegistered = false;

  private static registerX6Shape() {
    if (EntityListComponent.x6ShapeRegistered) return;
    Shape.HTML.register({
      shape: 'entity-card',
      effect: ['data'],
      html(cell: any) {
        const d = cell.getData() || {};
        const wrap = document.createElement('div');
        wrap.style.cssText = 'width:100%;height:100%;overflow:visible;background:rgba(30,30,40,0.95);border:1px solid rgba(255,255,255,0.1);border-radius:10px;padding:10px 12px;cursor:pointer;font-family:system-ui,-apple-system,sans-serif;box-sizing:border-box;transition:border-color 0.2s,box-shadow 0.2s;';
        wrap.innerHTML = `
          <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:6px">
            <span style="font-size:13px;font-weight:600;color:#f3f4f6;white-space:nowrap">${d.name || ''}</span>
            <span style="padding:1px 6px;border-radius:4px;font-size:9px;font-weight:600;letter-spacing:0.5px;text-transform:uppercase;background:${d.statusBg || 'transparent'};color:${d.statusFg || '#aaa'};margin-left:8px;flex-shrink:0">${d.status || ''}</span>
          </div>
          <div style="display:flex;flex-direction:column;gap:3px">${d.badgesHtml || ''}</div>
        `;
        wrap.addEventListener('mouseenter', () => {
          wrap.style.borderColor = 'rgba(168,85,247,0.5)';
          wrap.style.boxShadow = '0 0 20px rgba(168,85,247,0.2)';
        });
        wrap.addEventListener('mouseleave', () => {
          wrap.style.borderColor = 'rgba(255,255,255,0.1)';
          wrap.style.boxShadow = 'none';
        });
        return wrap;
      },
    });
    Shape.HTML.register({
      shape: 'import-node',
      effect: ['data'],
      html(cell: any) {
        const d = cell.getData() || {};
        const colors: Record<string, { border: string; bg: string; glow: string }> = {
          green: { border: 'rgba(34,197,94,0.6)', bg: 'rgba(34,197,94,0.08)', glow: 'rgba(34,197,94,0.15)' },
          blue: { border: 'rgba(59,130,246,0.6)', bg: 'rgba(59,130,246,0.08)', glow: 'rgba(59,130,246,0.15)' },
          red: { border: 'rgba(239,68,68,0.4)', bg: 'rgba(239,68,68,0.06)', glow: 'none' },
        };
        const c = colors[d.color] || colors['red'];
        const typeColors: Record<string, string> = { entity: '#fbbf24', function: '#60a5fa', feature: '#c084fc' };
        const typeFg = typeColors[d.type] || '#aaa';
        const wrap = document.createElement('div');
        wrap.style.cssText = `width:100%;height:100%;overflow:visible;background:${c.bg};border:1.5px solid ${c.border};border-radius:10px;padding:8px 12px;cursor:${d.color === 'blue' ? 'pointer' : 'default'};font-family:system-ui,-apple-system,sans-serif;box-sizing:border-box;transition:all 0.2s;${c.glow !== 'none' ? 'box-shadow:0 0 12px ' + c.glow : ''}`;
        wrap.innerHTML = `
          <div style="display:flex;align-items:center;gap:6px;margin-bottom:3px">
            <span style="padding:1px 5px;border-radius:3px;font-size:8px;font-weight:700;letter-spacing:0.5px;text-transform:uppercase;color:${typeFg};background:${typeFg}18">${d.type || ''}</span>
            <span style="font-size:12px;font-weight:600;color:#f3f4f6;white-space:nowrap">${d.name || ''}</span>
          </div>
          ${d.subtitle ? `<div style="font-size:9px;color:rgba(255,255,255,0.4)">${d.subtitle}</div>` : ''}
        `;
        if (d.color === 'blue') {
          wrap.addEventListener('mouseenter', () => { wrap.style.borderColor = 'rgba(59,130,246,0.9)'; wrap.style.boxShadow = '0 0 20px rgba(59,130,246,0.3)'; });
          wrap.addEventListener('mouseleave', () => { wrap.style.borderColor = c.border; wrap.style.boxShadow = c.glow !== 'none' ? '0 0 12px ' + c.glow : 'none'; });
        }
        return wrap;
      },
    });
    EntityListComponent.x6ShapeRegistered = true;
  }

  private initX6Graph() {
    if (!this.x6ContainerRef) return;
    // Was (2026-10-04, plan 6 final review, I1 — disposed unconditionally, including on the SAME live container):
    // // Plan 6 — two redraws after a Save (exitEntity's timeout and the list reload) stacked two graphs in one container,
    // // so a node showed twice. The old graph goes before a new one is drawn.
    // this.x6Graph?.dispose();
    // this.x6Graph = null;
    // Plan 6 (final review I1) — a second initX6Graph() on a container that wasn't recreated used to orphan the earlier
    // graph with its stale drawing (seen on Dev: a node shown twice after a Save). On the SAME container, redraw the
    // graph that is already there; dispose only a graph left on an old container — X6's dispose() restores the
    // container's original attributes (Angular's display style included), so it must never run on the live one.
    const live = this.x6ContainerRef.nativeElement as HTMLElement;
    if (this.x6Graph && this.x6Graph.container === live) { this.buildGraph(); return; }
    this.x6Graph?.dispose();
    this.x6Graph = null;
    EntityListComponent.registerX6Shape();
    const container = this.x6ContainerRef.nativeElement as HTMLElement;
    this.x6Graph = new Graph({
      container,
      ...X6_SYNC_RENDER,
      autoResize: true,
      background: { color: 'transparent' },
      panning: { enabled: true, eventTypes: ['leftMouseDown', 'rightMouseDown'] },
      mousewheel: { enabled: false },
      scaling: { min: 0.3, max: 2 },
      interacting: { nodeMovable: false },
    });

    this.x6Graph.on('node:click', ({ node }: any) => {
      const entityId = node.id;
      if (!this.entityList) return;
      const idx = this.entityList.findIndex(e => e.id === entityId);
      if (idx >= 0) this.editEntity(idx);
    });

    this.x6Graph.on('node:mouseenter', ({ node }: any) => {
      const connectedIds = this.getGraphChain(node.id);
      for (const cell of this.x6Graph!.getCells()) {
        const view = this.x6Graph!.findViewByCell(cell);
        if (!view) continue;
        if (cell.isNode()) {
          (view.container as HTMLElement).style.opacity = connectedIds.has(cell.id) ? '1' : '0.15';
          (view.container as HTMLElement).style.transition = 'opacity 0.2s';
        } else if (cell.isEdge()) {
          const src = (cell as any).getSourceCellId?.();
          const tgt = (cell as any).getTargetCellId?.();
          const edgeConnected = connectedIds.has(src) && connectedIds.has(tgt);
          (view.container as HTMLElement).style.opacity = edgeConnected ? '1' : '0.08';
          (view.container as HTMLElement).style.transition = 'opacity 0.2s';
        }
      }
    });

    this.x6Graph.on('node:mouseleave', () => {
      for (const cell of this.x6Graph!.getCells()) {
        const view = this.x6Graph!.findViewByCell(cell);
        if (!view) continue;
        (view.container as HTMLElement).style.opacity = '1';
      }
    });

    this.x6Ready = true;
    this.buildGraph();
  }

  buildGraph() {
    if (!this.x6Ready || !this.x6Graph) return;
    if (!this.entityList?.length) {
      this.x6Graph.clearCells();
      this.nonLinkedEntities = [];
      this.localEntities = [];
      this.hasLinkedEntities = false;
      return;
    }

    const model = buildOverviewModel(this.entityList);
    this.currentEdges = model.edges;
    this.nonLinkedEntities = model.nonLinked;
    this.localEntities = model.locals;
    this.hasLinkedEntities = model.nodes.length > 0;

    // Aliases so the untouched X6 edge/layout code below keeps compiling unchanged.
    const edges = model.edges;
    const linked = model.nodes;

    this.x6Graph.clearCells();
    if (!linked.length) return;

    for (const n of linked) {
      let badgesHtml = '';
      for (const ref of n.refs) {
        badgesHtml += `<div style="display:flex;align-items:center;padding:2px 8px;border-radius:6px;font-size:10px;font-weight:500;background:rgba(255,255,255,0.04);border:1px solid ${ref.color};color:${ref.color}">\u2192 ${ref.targetName}</div>`;
      }
      if (n.refs.length && n.aobs.length) {
        badgesHtml += `<div style="border-top:1px solid rgba(249,115,22,0.2);margin:4px 0"></div>`;
      }
      for (const aob of n.aobs) {
        badgesHtml += `<div style="display:flex;align-items:center;padding:2px 8px;border-radius:6px;font-size:10px;font-weight:500;background:rgba(249,115,22,0.15);color:rgba(251,146,60,0.9);border:1px solid rgba(249,115,22,0.2)">\u25c6 ${aob}</div>`;
      }
      if (!n.refs.length && !n.aobs.length) {
        badgesHtml = `<div style="font-size:10px;color:rgba(255,255,255,0.35)">${n.fieldCount} fields</div>`;
      }

      const sc = this.getStatusColors(n.status);

      this.x6Graph.addNode({
        id: n.id,
        x: 0,
        y: 0,
        width: n.width,
        height: n.height,
        shape: 'entity-card',
        data: {
          name: n.name,
          status: n.status,
          statusBg: sc.bg,
          statusFg: sc.fg,
          badgesHtml,
        },
        ports: {
          groups: {
            out: {
              position: 'right',
              attrs: {
                circle: { r: 6, magnet: true, stroke: 'rgba(168,85,247,0.8)', fill: 'rgba(168,85,247,0.5)', strokeWidth: 2 },
              },
            },
            in: {
              position: 'left',
              attrs: {
                circle: { r: 6, magnet: true, stroke: 'rgba(168,85,247,0.8)', fill: 'rgba(168,85,247,0.5)', strokeWidth: 2 },
              },
            },
          },
          items: [
            { id: `${n.id}__out`, group: 'out' },
            { id: `${n.id}__in`, group: 'in' },
          ],
        },
      });
    }

    for (const edge of edges) {
      this.x6Graph.addEdge({
        source: { cell: edge.sourceId, port: `${edge.sourceId}__out` },
        target: { cell: edge.targetId, port: `${edge.targetId}__in` },
        router: { name: 'manhattan', args: { padding: 20 } },
        connector: { name: 'rounded', args: { radius: 8 } },
        attrs: {
          line: {
            stroke: edge.color,
            strokeWidth: 2,
            targetMarker: { name: 'classic', size: 8 },
          },
        },
        labels: [],
      });
    }

    const positions = layoutOverview(linked, edges);
    for (const n of linked) {
      const p = positions.get(n.id);
      const cell = this.x6Graph.getCellById(n.id) as any;
      // layoutOverview already returns top-left, so no centre conversion here.
      if (cell && p) cell.setPosition(p.x, p.y);
    }

    this.x6Graph.zoomToFit({ padding: 40, maxScale: 1.2 });
  }

  private getGraphChain(nodeId: string): Set<string> {
    return graphChain(this.currentEdges, nodeId);
  }

  pendingImportNode: typeof this.importNodes[0] | null = null;

  onImportCardClick(node: typeof this.importNodes[0]) {
    if (node.color === 'blue') {
      this.pendingImportNode = node;
    }
  }

  confirmImport() {
    const node = this.pendingImportNode;
    if (!node) return;
    this.pendingImportNode = null;
    // Was (2026-10-30): the entity's import had no catch, so a failure reached nobody.
    if (node.type === 'entity') {
      this.selectedEntity = node.importId;
      this.confirmWaits.saving(ENTITY_IMPORT_COMMIT);
      this.addOOTBEntity().then(() => {
        this.buildImportGraph();
        this.confirmWaits.report(ENTITY_IMPORT_COMMIT, { outcome: 'saved', saved: { imported: node.name } });
      }).catch((error) => {
        console.error('Error importing entity:', error);
        this.saveFailure = (error as Error)?.message || 'The import failed. Please try again.';
        this.confirmWaits.report(ENTITY_IMPORT_COMMIT, { outcome: 'failed', reason: this.saveFailure });
      });
    } else if (node.type === 'function') {
      this.selectedGlobalFunction = node.importId;
      this.importGlobalFunction().then(() => this.buildImportGraph());
    }
  }

  cancelImport() {
    this.pendingImportNode = null;
    this.confirmWaits.report(ENTITY_IMPORT_COMMIT, { outcome: 'discarded' });
  }

  onImportCardHover(nodeId: string | null) {
    this.hoveredImportNode = nodeId;
    if (!nodeId) {
      this.highlightedIds = new Set();
      this.highlightUpstream = new Set();
      this.highlightDownstream = new Set();
      return;
    }

    const all = new Set<string>([nodeId]);
    const up = new Set<string>();
    const down = new Set<string>();

    const collectUpstream = (id: string) => {
      const node = this.importNodes.find(n => n.id === id);
      if (!node) return;
      for (const dep of node.deps) {
        if (!up.has(dep)) {
          up.add(dep);
          all.add(dep);
          collectUpstream(dep);
        }
      }
    };
    const collectDownstream = (id: string) => {
      const node = this.importNodes.find(n => n.id === id);
      if (!node) return;
      for (const dep of node.dependents) {
        if (!down.has(dep)) {
          down.add(dep);
          all.add(dep);
          collectDownstream(dep);
        }
      }
    };
    collectUpstream(nodeId);
    collectDownstream(nodeId);

    this.highlightedIds = all;
    this.highlightUpstream = up;
    this.highlightDownstream = down;
  }

  isImportCardHighlighted(nodeId: string): boolean {
    if (!this.hoveredImportNode) return false;
    return this.highlightedIds.has(nodeId);
  }

  getImportCardGlow(nodeId: string): string {
    if (!this.hoveredImportNode) return 'none';
    if (nodeId === this.hoveredImportNode) return '0 0 16px rgba(168,85,247,0.5)';
    if (this.highlightUpstream.has(nodeId)) return '0 0 12px rgba(34,197,94,0.35)';
    if (this.highlightDownstream.has(nodeId)) return '0 0 12px rgba(59,130,246,0.35)';
    return 'none';
  }

  buildImportGraph() {
    const nodes: typeof this.importNodes = [];
    const ootbIdSet = new Set(this.ootb.map(e => e.id));

    for (const entity of this.ootb) {
      const imported = this.isOotbAlreadyImported(entity.id);
      const entityRefs = entity.data
        .filter(f => f.type === 'ENTITY_REFERENCE' && f.referenceEntityName && f.name !== 'parent_id' && f.name !== 'id')
        .map(f => f.referenceEntityName)
        .filter(ref => ootbIdSet.has(ref) && ref !== entity.id);

      const depsImported = entityRefs.every(ref => this.isOotbAlreadyImported(ref));
      const color = imported ? 'green' : (depsImported ? 'blue' : 'red');

      nodes.push({
        id: `ootb_${entity.id}`,
        name: entity.name,
        type: 'entity',
        color,
        subtitle: `${entity.data.length} fields`,
        importId: entity.id,
        deps: entityRefs.map(r => `ootb_${r}`),
        dependents: [],
      });
    }

    for (const func of this.globalFunctions) {
      const imported = this.isFunctionAlreadyImported(func);
      const canImport = this.canImportFunction(func);
      const color = imported ? 'green' : (canImport ? 'blue' : 'red');

      const entityDeps = this.collectReferencedEntities(func);
      const ootbDeps = entityDeps.filter(ref => ootbIdSet.has(ref));

      nodes.push({
        id: `func_${func.name}`,
        name: func.name,
        type: 'function',
        color,
        subtitle: `${func.inputs?.length || 0} inputs`,
        importId: func.name,
        deps: ootbDeps.map(r => `ootb_${r}`),
        dependents: [],
      });
    }

    for (const feat of FEATURE_COMPONENTS) {
      const allReady = feat.requiredEntities.every(id => this.isOotbAlreadyImported(id)) &&
        feat.requiredFunctions.every(name => this.localFunctions.some(f => f.name === name));
      const color = allReady ? 'green' : 'red';

      nodes.push({
        id: `feat_${feat.id}`,
        name: feat.name,
        type: 'feature',
        color,
        subtitle: feat.description,
        importId: feat.id,
        deps: [
          ...feat.requiredEntities.filter(id => ootbIdSet.has(id)).map(id => `ootb_${id}`),
          ...feat.requiredFunctions.map(name => `func_${name}`),
        ],
        dependents: [],
      });
    }

    for (const node of nodes) {
      for (const depId of node.deps) {
        const depNode = nodes.find(n => n.id === depId);
        if (depNode && !depNode.dependents.includes(node.id)) {
          depNode.dependents.push(node.id);
        }
      }
    }

    this.importNodes = nodes;
  }

  /** Opens an entity from the Foblex graph — same behaviour as X6's node:click handler. */
  onGraphNodeClick(entityId: string) {
    if (!this.entityList) return;
    const idx = this.entityList.findIndex(e => e.id === entityId);
    if (idx >= 0) this.editEntity(idx);
  }

  toggleGraphEngine() {
    this.graphEngine = this.graphEngine === 'foblex' ? 'x6' : 'foblex';
    if (this.graphEngine !== 'x6') return;
    this.remeasureX6();
    this.remeasureDetailX6();
  }

  /**
   * The detail view's X6 counterpart. It needs more than a re-measure: while Foblex owns the view
   * the container is `display: none` from the moment the editor opens, and X6 never builds a graph
   * into a hidden container — so on the first switch there is nothing to resize and we must
   * initialise instead. Same parent-measuring rule as `remeasureX6`.
   */
  private remeasureDetailX6() {
    if (!this.createEntityFlag) return;
    requestAnimationFrame(() => {
      const live = this.detailContainerRef?.nativeElement as HTMLElement | undefined;
      if (!this.detailGraph || !live?.classList.contains('x6-graph')) { this.initDetailGraph(); return; }
      const el = this.detailContainerRef?.nativeElement as HTMLElement | undefined;
      const host = el?.parentElement;
      if (!el || !host) return;
      this.detailGraph.resize(host.clientWidth, host.clientHeight);
      this.detailGraph.zoomToFit({ padding: 40, maxScale: 1.2 });
    });
  }

  /**
   * Bring the X6 overview graph back when the engine switches to X6.
   *
   * Two traps, both stemming from the same fact: **X6 does not mount into a hidden container.**
   *
   * 1. While Foblex owns the view the container is `display: none`, so any `initX6Graph()` that
   *    runs meanwhile (notably the one `exitEntity()` fires when leaving the entity editor)
   *    silently produces no graph — `.x6-graph` is never even added to the element. So on switching
   *    we must check whether the LIVE container is actually mounted, and initialise if it is not.
   * 2. Measure the PARENT, never the container. X6's stylesheet sets `.x6-graph { position: relative }`,
   *    which beats Tailwind's `.absolute`, making `inset-0` inert and collapsing the container's
   *    height to 0. The container's real size comes from the inline `width`/`height` X6 writes in
   *    `resize()` — those are load-bearing and must never be cleared. A hidden container also
   *    reports `clientWidth === 0`, and `resize(0, 0)` writes an inline zero size that permanently
   *    overrides `w-full h-full`.
   */
  private remeasureX6() {
    const el = this.x6ContainerRef?.nativeElement as HTMLElement | undefined;
    const host = el?.parentElement;
    if (!el || !host) return;

    requestAnimationFrame(() => {
      const live = this.x6ContainerRef?.nativeElement as HTMLElement | undefined;
      if (!live) return;

      if (!this.x6Graph || !live.classList.contains('x6-graph')) {
        this.x6Graph?.dispose();
        this.x6Graph = null;
        this.x6Ready = false;
        this.initX6Graph();
        return;
      }

      this.x6Graph.resize(host.clientWidth, host.clientHeight);
      this.x6Graph.zoomToFit({ padding: 40, maxScale: 1.2 });
    });
  }

  zoomIn() {
    if (this.graphEngine === 'foblex') { this.entityGraph?.zoomIn(); return; }
    if (!this.x6Graph) return;
    this.x6Graph.zoom(0.15);
  }

  zoomOut() {
    if (this.graphEngine === 'foblex') { this.entityGraph?.zoomOut(); return; }
    if (!this.x6Graph) return;
    this.x6Graph.zoom(-0.15);
  }

  zoomFit() {
    if (this.graphEngine === 'foblex') { this.entityGraph?.zoomFit(); return; }
    if (!this.x6Graph) return;
    this.x6Graph.zoomToFit({ padding: 40, maxScale: 1.2 });
  }

  private getStatusColors(status: string): { bg: string; fg: string } {
    const map: Record<string, { bg: string; fg: string }> = {
      'SYNCED': { bg: 'rgba(34,197,94,0.15)', fg: '#4ade80' },
      'READY': { bg: 'rgba(59,130,246,0.15)', fg: '#60a5fa' },
      'PENDING': { bg: 'rgba(249,115,22,0.15)', fg: '#fb923c' },
      'LOCAL': { bg: 'rgba(234,179,8,0.15)', fg: '#facc15' },
      'NEW': { bg: 'rgba(168,85,247,0.15)', fg: '#c084fc' },
      'ERROR': { bg: 'rgba(239,68,68,0.15)', fg: '#f87171' },
    };
    return map[status] || map['LOCAL'];
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'SYNCED': return 'status-synced';
      case 'READY': return 'status-ready';
      case 'PENDING': return 'status-pending';
      case 'LOCAL': return 'status-local';
      case 'NEW': return 'status-new';
      case 'ERROR': return 'status-error';
      default: return 'status-local';
    }
  }

  editFromGraph(entityId: string, event: MouseEvent) {
    event.stopPropagation();
    if (!this.entityList) return;
    const idx = this.entityList.findIndex(e => e.id === entityId);
    if (idx >= 0) this.editEntity(idx);
  }
}
